import {
  ArrowRight,
  ArrowUpRight,
  Braces,
  ChevronDown,
  CalendarClock,
  CalendarOff,
  Car,
  Check,
  CircleAlert,
  Handshake,
  KeyRound,
  LayoutTemplate,
  ListFilter,
  Mail,
  MessageSquare,
  Plus,
  Rocket,
  TriangleAlert,
  Upload,
  UserRound,
  WandSparkles,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CampaignDraft } from '../../api/comms';
import { Spotlight } from '../../components/tour/Spotlight';
import { ApiError } from '../../api/envelope';
import type {
  AudienceMember,
  AudienceMeta,
  Campaign,
  CampaignAudience,
  CampaignAudienceFilters,
  CampaignChannel,
  CampaignSendMode,
  DispatchAudience,
  CampaignLinkTracking,
  Inbox,
  MergeField,
  SendingOptions,
  SmsSendingOptions,
} from '../../api/types';
import { identityLine, needsReauth } from '../../lib/channelMeta';
import { summarizeLinks, tidyTrackingName } from '../../lib/campaignLinks';
import { htmlToText } from '../../lib/htmlText';
import { priceShortened } from '../../lib/shortLinks';
import { useLoad } from '../../lib/useLoad';
import { tenantCurrencySymbol } from '../../lib/tenantCurrency';
import { useTimeFormat } from '../../lib/tenantTime';
import { CAMPAIGN_MAX_SEGMENTS, segmentsFor, smsCostLabel, toGsmSafe } from '../../lib/smsSegments';
import { instantFromWall, wallInZone } from '../../lib/timeZone';
import {
  ADDRESS_FIELDS,
  type AudienceFilterState,
  deliveryChoice,
  describeFilters,
  effectiveSendMode as resolveSendMode,
  BOOKING_PRESETS,
  type BookingFilterState,
  clockToMinutes,
  FREQUENCY_CAPS,
  minutesToClock,
  EMPTY_BOOKINGS,
  emptyFilterState,
  fromApiFilters,
  isSmsOptions,
  memberAddress,
  mergeValuesOf,
  parseCustomList,
  previewMerge,
  PRIORITY_LABELS,
  RECIPIENT_MERGE_FIELDS,
  scheduleBlocker,
  sendModeSummary,
  smsPaceSummary,
  smsReserveNote,
  toApiFilters,
  toPoolFilters,
  withBaselineFields,
  DEFAULT_LINK_TEXT,
  defaultFooterText,
} from './campaignTools';
import { CampaignPreflightCard, type PreflightRequest } from './CampaignPreflight';
import { type PreflightAction, type PreflightGroup, type PreflightRow, replaceOnce } from './preflight';
import { openSettingsTab } from './settingsTab';
import { boldSegments, campaignTourMoments } from './campaignTour';
import { useComms } from './context';
import { RichText, type RichTextHandle, type RichTextValue } from './RichText';
import { useInfiniteScroll } from './useInfiniteScroll';

const STEPS = ['Audience', 'Message', 'Throttle', 'Review & send'] as const;

const AUDIENCES: {
  key: CampaignAudience;
  label: string;
  icon: typeof UserRound;
}[] = [
  { key: 'customers', label: 'Customers', icon: UserRound },
  { key: 'affiliates', label: 'Affiliates', icon: Handshake },
  { key: 'drivers', label: 'Chauffeurs / drivers', icon: Car },
  { key: 'custom', label: 'Your own list', icon: Upload },
];

const DISPATCH_AUDIENCES: DispatchAudience[] = ['customers', 'affiliates', 'drivers'];

/** Which list the recipient picker is showing — see `pickerScope` below. */
type PickerScope = 'pool' | 'selected';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  active: 'Active',
  inactive: 'Inactive',
};

/**
 * The 4-step campaign builder (design step bar + cards), on the real API.
 *
 * `draft` reopens a saved draft for editing; every field below seeds from it in
 * a lazy initializer rather than an effect, so the caller must remount (key by
 * campaign id) to switch drafts — which is also what discards a half-finished
 * edit when the user starts a new campaign.
 */
export function CampaignBuilder({
  draft = null,
  initialStep = 1,
  initialChannel = 'email',
  onLaunched,
  onSavedDraft,
  tour = false,
  onTourEnd,
}: {
  draft?: Campaign | null;
  /** Where to open. A resend arrives with its audience and copy already decided,
   *  so it lands on Review & send rather than walking the wizard again. */
  initialStep?: number;
  /** Only read when there is no draft — an existing one carries its own. */
  initialChannel?: CampaignChannel;
  onLaunched: (campaign: Campaign) => void;
  onSavedDraft: (campaign: Campaign) => void;
  /** Open with the "Try a test campaign" walkthrough running (campaignTour.ts). */
  tour?: boolean;
  onTourEnd?: () => void;
}) {
  const { source, inboxes, templateBlocks, noteSendDenied, capabilities, reloadInboxes } = useComms();
  // The tenant's clock, and the only one this screen may think in. A schedule
  // typed here is a wall time in THEIR zone — see instantFromWall.
  const { zone, zoneLabel: tenantZoneLabel, fmtDateTime } = useTimeFormat();
  // Whether this agent can send a campaign themselves, or only submit one. The
  // server decides either way; this only picks the wording, so a stale answer
  // mislabels a button rather than granting anything.
  const mayApprove = capabilities?.access.features.campaigns !== 'draft';
  /**
   * What this campaign goes out as. Fixed on the first save and never changed
   * after it — the audience is addressed by a different column, the picks are a
   * different list and the body is a different field, so flipping it under an
   * existing draft would leave people picked by email being texted. A different
   * channel is a different campaign, and the server refuses the change too.
   */
  const [channel, setChannel] = useState<CampaignChannel>(draft?.channel ?? initialChannel);
  const isSms = channel === 'sms';
  /**
   * Switching it is only possible before the first save, and it resets the
   * picks and the sender with it. Both are keyed to the old channel — a list of
   * ticked email addresses means nothing to a text campaign, and the mailbox it
   * was going to leave from is not a phone number — so carrying them across
   * would show an audience and a sender that quietly do not apply.
   */
  const switchChannel = (next: CampaignChannel) => {
    if (draft || next === channel) return;
    setChannel(next);
    setInboxId(null);
    setCustomText('');
    setCustomEdited(true);
    setFilterStates((current) => {
      const reset = { ...current };
      for (const key of DISPATCH_AUDIENCES) {
        const state = reset[key];
        if (state) reset[key] = { ...state, mode: 'all', selected: [], excluded: [] };
      }
      return reset;
    });
  };
  const emailInboxes = useMemo(
    () => inboxes.filter((inbox): inbox is Inbox & { channelType: 'email' } => inbox.channelType === 'email'),
    [inboxes],
  );
  /**
   * The numbers a text campaign can leave from. WhatsApp rides the Twilio
   * adapter and is deliberately NOT here: a bulk send there needs approved
   * templates rather than free text, which this builder has no way to compose —
   * so the server refuses it and the picker never offers it.
   */
  const smsInboxes = useMemo(
    () =>
      inboxes.filter(
        (inbox) =>
          (inbox.channelType === 'twilio' && inbox.channel?.medium !== 'whatsapp') ||
          inbox.channelType === 'ringcentral',
      ),
    [inboxes],
  );
  const senderInboxes = isSms ? smsInboxes : emailInboxes;
  /**
   * Nothing to send from on this channel — no mailbox (or no SMS number)
   * connected, or none this person can see. Every step still opens, so the
   * builder can be learnt, but nothing can be saved or sent; the notice at the
   * top says so on every step rather than leaving "pick a sending mailbox" to
   * point at an empty picker.
   */
  const noSender = senderInboxes.length === 0;
  const otherChannelHasSender = (isSms ? emailInboxes : smsInboxes).length > 0;
  // Permissive while /meta is in flight would offer a button the server then
  // refuses; connecting is an admin act, so this one waits for the answer.
  const mayConnect = capabilities?.access.features.connectChannels === true;

  const [step, setStep] = useState(initialStep);
  const [audience, setAudience] = useState<CampaignAudience>(draft?.audienceType ?? 'customers');
  const [customText, setCustomText] = useState(() =>
    (draft?.customRecipients ?? [])
      .map((row) => {
        const address = row.phoneNumber ?? row.email ?? '';
        return row.name ? `${address}, ${row.name}` : address;
      })
      .join('\n'),
  );
  // Whether that text has been touched since it was seeded. A resend arrives
  // with a list the server RESOLVED — the exact rows a campaign reached — and
  // rendering it as `email, name` lines and reparsing them is lossy: a name
  // holding a comma comes back rejoined with a space, so {{name}} renders
  // something other than what went out. An untouched list is therefore passed
  // through as it was received, and only an edited one is reparsed.
  const [customEdited, setCustomEdited] = useState(false);
  const [audienceCounts, setAudienceCounts] = useState<Partial<Record<CampaignAudience, number>>>({});
  /**
   * How many of each untargeted audience have opted out. Kept beside the chip
   * counts and not only on the filtered preview, because an audience with no
   * filters set never reaches that preview at all — its number comes from here,
   * and reading the opt-out figure from only one of the two paths left it blank
   * on exactly the campaigns nobody had narrowed yet.
   */
  const [audienceOptedOut, setAudienceOptedOut] = useState<Partial<Record<CampaignAudience, number>>>(
    {},
  );
  const [audienceErrors, setAudienceErrors] = useState<Partial<Record<CampaignAudience, string>>>({});
  const [meta, setMeta] = useState<AudienceMeta | null>(null);
  // Only an audience the draft actually targeted seeds a slot. Leaving the rest
  // empty lets the audience-meta effect below fill each one's defaults, so a
  // draft saved without filters reopens exactly like a fresh builder.
  const [filterStates, setFilterStates] = useState<Partial<Record<DispatchAudience, AudienceFilterState>>>(
    () =>
      draft && draft.audienceType !== 'custom' && draft.audienceFilters
        ? { [draft.audienceType]: fromApiFilters(draft.audienceFilters) }
        : {},
  );
  const [filteredCount, setFilteredCount] = useState<number | null>(null);
  /**
   * How many of the audience have opted out. Held beside the count rather than
   * folded into it — the recipient number must not move when the override is
   * flipped, or the one figure an operator is deciding on changes under them.
   */
  const [optedOutCount, setOptedOutCount] = useState(0);
  /**
   * Whether the operator has opened the recipient list to choose from it.
   *
   * Held rather than derived from `filterState`, because "choose from the list"
   * covers BOTH stored shapes — everyone-minus-a-few and only-these — and a chip
   * that snapped back to "All matching" the moment somebody pressed Select all
   * would be describing the storage rather than what they are doing. It only
   * ever forces the list OPEN: a saved draft that carries picks opens on it
   * without this having to be seeded per audience.
   */
  const [pickingOpen, setPickingOpen] = useState(false);
  // Which list the picker is showing. 'pool' is everyone the filters match — what
  // picks are made from; 'selected' is who is actually on the campaign. Without
  // the second one a hand-picked list was unreadable: the ticks were scattered
  // across pages of thousands and nothing on screen listed just them.
  const [pickerScope, setPickerScope] = useState<PickerScope>('pool');
  const [members, setMembers] = useState<AudienceMember[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersPage, setMembersPage] = useState(1);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [name, setName] = useState(draft?.name ?? '');
  const [inboxId, setInboxId] = useState<number | null>(draft?.inboxId ?? null);
  const [fromName, setFromName] = useState(draft?.fromName ?? '');
  const [subject, setSubject] = useState(draft?.subject ?? '');
  const [body, setBody] = useState<RichTextValue>(() =>
    draft?.bodyHtml
      ? { html: draft.bodyHtml, text: draft.bodyText ?? htmlToText(draft.bodyHtml) }
      : { html: '', text: '' },
  );
  const [bodySms, setBodySms] = useState(draft?.bodySms ?? '');
  /**
   * What this copy costs, counted as the operator types by the console's mirror
   * of the service's own function (lib/smsSegments). Not a round trip per
   * keystroke, and not a character count either — segments are what the carrier
   * meters and what the number's throughput is spent in, and one pasted curly
   * apostrophe doubles them without changing the character count at all.
   */
  /**
   * `null` until somebody touches it; then the CHANNEL's default applies — on
   * for email, off for a text, where it means short links that cost
   * characters (services/shortLinks.js).
   */
  const [trackClicksChoice, setTrackClicksChoice] = useState<boolean | null>(
    draft ? Boolean(draft.trackClicks) : null,
  );
  const trackClicks = trackClicksChoice ?? !isSms;
  const setTrackClicks = (next: (on: boolean) => boolean) => setTrackClicksChoice(next(trackClicks));
  // What a text's short links start with — the tenant's verified domain, or the
  // service. Read once; the count below prices the text as it will be sent.
  const shortLinkState = useLoad(
    () => (isSms ? source.api.getShortLinkDomain() : Promise.resolve(null)),
    [source, isSms],
  );
  const shortBase = shortLinkState.data?.shortBase ?? null;
  // The company's footer wording (Campaigns → Settings → Opting out) — what an
  // email whose own footer fields stay empty goes out with, so it is what the
  // fields show greyed out. A failed read falls back to the built-in wording,
  // which is what the placeholder said before there was a company default.
  const campaignSettings = useLoad(
    () => (isSms ? Promise.resolve(null) : source.api.getCampaignSettings()),
    [source, isSms],
  );
  const companyFooter = campaignSettings.data?.footer ?? null;
  const smsCost = useMemo(
    () => segmentsFor(trackClicks && isSms && shortBase ? priceShortened(bodySms, shortBase) : bodySms),
    [bodySms, trackClicks, isSms, shortBase],
  );
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState<'generate' | 'improve' | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  /**
   * Whether AI assist can run at all: a key resolves for this tenant, their own
   * or the platform's. Asked up front (GET /ai/models, the probe AI settings
   * reads), so a builder with no key says so beside the buttons and points at
   * the screen that fixes it, rather than failing on the first Generate. Null
   * is "not known" — still asking, or the probe failed — and leaves the card
   * as it always was. Re-asked on **Check again**, for the operator who went
   * off to add a key in another tab.
   */
  const aiLevel = capabilities?.access.features.ai;
  // Permissive while /meta is in flight, like every capability read here.
  const aiOffered = aiLevel !== 'off';
  const mayManageAi = aiLevel === 'manage';
  const [aiReady, setAiReady] = useState<boolean | null>(null);
  const [aiProbe, setAiProbe] = useState(0);
  useEffect(() => {
    if (!aiOffered) return;
    let cancelled = false;
    source.api.getAiAvailability().then(
      (answer) => {
        if (!cancelled) setAiReady(answer.available);
      },
      () => {
        if (!cancelled) setAiReady(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [source, aiOffered, aiProbe]);
  // In a new tab: this screen may be holding a campaign nobody has saved yet.
  const openAiSettings = () => openSettingsTab('/ai', source.embedded);
  const [throttle, setThrottle] = useState(draft?.throttlePerMinute ?? 30);
  // Defaults ON for a new campaign, which is also the server's default: the
  // compliant answer should be the one an operator has to turn off. `?? true`
  // rather than `!== false` so a draft saved by a build that predates this
  // reopens the way it will actually send.
  const [unsubFooter, setUnsubFooter] = useState(draft?.includeUnsubscribe ?? true);
  /**
   * Contact people who have opted out. The inverse default of the toggle above
   * and deliberately so: `?? false`, so a draft saved before this existed, and
   * every new campaign, screens the list exactly as it always has. Nobody
   * arrives at the compliant answer by choosing it.
   */
  const [contactOptedOut, setContactOptedOut] = useState(draft?.contactOptedOut ?? false);
  // The footer's wording is a rarely-touched detail: folded away unless this
  // campaign already has its own.
  const [insertOpen, setInsertOpen] = useState(false);
  const [unsubWordingOpen, setUnsubWordingOpen] = useState(
    Boolean(draft?.unsubscribeFooterText || draft?.unsubscribeLinkText),
  );
  // Held as '' rather than null so the inputs stay controlled; '' is sent as
  // null, which is what the server reads as "use the default sentence".
  const [unsubText, setUnsubText] = useState(draft?.unsubscribeFooterText ?? '');
  const [unsubLinkText, setUnsubLinkText] = useState(draft?.unsubscribeLinkText ?? '');
  /**
   * Whether links to the tenant's website carry this campaign's name, so the
   * visits they bring are reported under it. ON for a new email campaign — a
   * mail with no link to the site tags nothing, so the default costs nothing —
   * and whatever a saved draft says otherwise, which is also what makes a draft
   * from before this existed reopen the way it will actually send.
   */
  const [trackLinks, setTrackLinks] = useState(draft ? Boolean(draft.utmCampaign) : true);
  /**
   * What the operator typed, or null while the name simply follows the campaign
   * name — a draft that already has one keeps it, because the report has been
   * counting visits under it and renaming the campaign must not split them.
   */
  const [trackingTyped, setTrackingTyped] = useState<string | null>(draft?.utmCampaign ?? null);
  const trackingName = tidyTrackingName(trackingTyped ?? name);
  /**
   * Per-link click tracking — body links go through our redirector so Results
   * can say who pressed what. ON for a new email campaign, matching the server;
   * a saved draft keeps what it had.
   */
  /**
   * Quiet hours. `null` until the operator touches it, and then the default is
   * the CHANNEL's: on for a text (a 2am text is what carriers and the TCPA
   * expect you never to send), off for email. Held as a tri-state because the
   * channel can still change before the first save.
   */
  const [quietChoice, setQuietChoice] = useState<boolean | null>(
    draft ? draft.sendWindowStart != null : null,
  );
  const [windowStart, setWindowStart] = useState(draft?.sendWindowStart ?? 8 * 60);
  const [windowEnd, setWindowEnd] = useState(draft?.sendWindowEnd ?? 21 * 60);
  const quietOn = quietChoice ?? isSms;
  const [frequencyCap, setFrequencyCap] = useState<number | null>(draft?.frequencyCapHours ?? null);
  // Leave out addresses that bounced before — offered by the pre-flight review,
  // never on unless the operator says so.
  const [skipBounced, setSkipBounced] = useState(draft?.skipBounced ?? false);
  const [testTo, setTestTo] = useState('');
  const [testState, setTestState] = useState<{ busy: boolean; note: string | null; error: boolean }>({
    busy: false,
    note: null,
    error: false,
  });
  // The tenant's own hosts — the only ones a link is tagged for — or 'failed'.
  const [linkSites, setLinkSites] = useState<CampaignLinkTracking | 'failed' | null>(null);
  const [sendMode, setSendMode] = useState<CampaignSendMode>(
    draft?.status === 'scheduled' ? draft.sendMode ?? 'spread' : 'spread',
  );
  /**
   * Now or later. A scheduled draft reopens on 'later' with its own time in the
   * box, because that is the state the operator left it in — landing on "Send
   * campaign" over a campaign that is already booked for Friday is how somebody
   * mails three thousand people by pressing the obvious button.
   */
  const [whenMode, setWhenMode] = useState<'now' | 'later'>(
    draft?.status === 'scheduled' ? 'later' : 'now',
  );
  // The `datetime-local` value: a wall clock with no zone on it, read as the
  // TENANT's. Seeded from the stored instant through the tenant's zone, never
  // through the browser's.
  const [scheduleWall, setScheduleWall] = useState(() =>
    draft?.status === 'scheduled' && draft.scheduledAt
      ? wallInZone(new Date(draft.scheduledAt), zone)
      : '',
  );
  const [sendingOptions, setSendingOptions] = useState<SendingOptions | SmsSendingOptions | null>(
    null,
  );
  const [busy, setBusy] = useState<'draft' | 'send' | 'schedule' | 'unschedule' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Seeded with the recipient tags, not empty: they work against every backend,
  // and an unreachable /campaigns/merge_fields must not take the panel with it.
  const [mergeFields, setMergeFields] = useState<MergeField[]>(RECIPIENT_MERGE_FIELDS);
  // Which field a data-point chip drops into. Tracked on focus rather than
  // guessed: the subject is where {{name}} earns the most and is the field a
  // chip would otherwise be useless for.
  const [insertTarget, setInsertTarget] = useState<'subject' | 'body'>('body');
  const fileInput = useRef<HTMLInputElement>(null);
  const subjectInput = useRef<HTMLInputElement>(null);
  const bodyEditor = useRef<RichTextHandle>(null);

  // What each audience supports (statuses/priority/address) comes from the live
  // dispatch schema. Without it — older backend, dispatch outage — the targeting
  // UI stays hidden and the builder behaves exactly as before filters existed.
  useEffect(() => {
    let cancelled = false;
    source.api.getAudienceMeta(channel).then(
      ({ meta: loaded }) => {
        if (cancelled) return;
        setMeta(loaded);
        setFilterStates((current) => {
          const next = { ...current };
          for (const key of DISPATCH_AUDIENCES) {
            if (!next[key]) next[key] = emptyFilterState(loaded[key]?.defaultStatuses ?? []);
          }
          return next;
        });
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, [source, channel]);

  // The tenant's data points, values included. A failure is silent by design —
  // the panel keeps its baseline recipient tags, which is what an older backend
  // (or a dispatch outage) can still substitute.
  useEffect(() => {
    let cancelled = false;
    source.api.listMergeFields().then(
      (fields) => {
        if (!cancelled) setMergeFields(withBaselineFields(fields));
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, [source]);

  // Which hosts count as the tenant's website, from the same resolver the send
  // uses. Email only: a text campaign tags nothing.
  useEffect(() => {
    if (isSms) return;
    let cancelled = false;
    source.api.getCampaignLinkTracking().then(
      (loaded) => {
        if (!cancelled) setLinkSites(loaded);
      },
      () => {
        if (!cancelled) setLinkSites('failed');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [source, isSms]);

  // Real audience sizes for the chips — one preview call per dispatch audience.
  // A failure is kept, not swallowed: a missing dispatch grant used to leave the
  // chip silently countless.
  //
  // The CHANNEL is what decides which column dispatch is read out of, so it is
  // not optional here: without it a text campaign counted everyone who has an
  // EMAIL and put that number on the chip, which is a different set of people
  // and usually a much larger one. Re-runs when the channel is switched,
  // because the same audience is a different size on the other channel.
  useEffect(() => {
    let cancelled = false;
    for (const key of DISPATCH_AUDIENCES) {
      source.api.previewAudience({ audienceType: key, channel }).then(
        (preview) => {
          if (cancelled) return;
          setAudienceCounts((current) => ({ ...current, [key]: preview.count }));
          setAudienceOptedOut((current) => ({ ...current, [key]: preview.optedOutCount ?? 0 }));
        },
        (e: unknown) => {
          if (cancelled) return;
          setAudienceErrors((current) => ({
            ...current,
            [key]: e instanceof ApiError ? e.message : 'Could not load this audience.',
          }));
        },
      );
    }
    return () => {
      cancelled = true;
    };
  }, [source, channel]);

  const seededRecipients = draft?.audienceType === 'custom' ? (draft.customRecipients ?? null) : null;
  const customRows = useMemo(
    () =>
      !customEdited && seededRecipients ? seededRecipients : parseCustomList(customText, channel),
    [customEdited, seededRecipients, customText, channel],
  );

  // The tenant's real values, so the preview below shows the mail as it will be
  // sent rather than leaving {{company_phone}} on screen.
  const tenantMerge = useMemo(() => mergeValuesOf(mergeFields), [mergeFields]);
  const ownHosts = useMemo(
    () => (linkSites && linkSites !== 'failed' ? linkSites.payload.map((site) => site.host) : []),
    [linkSites],
  );
  /**
   * Which of the body's links will carry the name. Counted on the MERGED body,
   * because the send tags after the merge — a link built from
   * {{company_website}} is judged by where it actually goes.
   */
  const linkSummary = useMemo(
    () => summarizeLinks(previewMerge(body.html, undefined, tenantMerge), ownHosts),
    [body.html, tenantMerge, ownHosts],
  );
  const mergeGroups = useMemo(
    () =>
      [
        { key: 'recipient' as const, label: 'Each recipient' },
        { key: 'company' as const, label: 'Your company' },
      ]
        .map((group) => ({ ...group, fields: mergeFields.filter((f) => f.group === group.key) }))
        .filter((group) => group.fields.length > 0),
    [mergeFields],
  );

  /** Drop a tag where the caret is, in whichever field was last focused. */
  const insertTag = (tag: string) => {
    if (insertTarget === 'subject') {
      const input = subjectInput.current;
      if (!input) return;
      const at = input.selectionStart ?? subject.length;
      const to = input.selectionEnd ?? at;
      setSubject(`${subject.slice(0, at)}${tag}${subject.slice(to)}`);
      // The caret has to be restored after React writes the new value back.
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(at + tag.length, at + tag.length);
      });
      return;
    }
    bodyEditor.current?.insert(tag);
  };

  const dispatchAudience: DispatchAudience | null = audience === 'custom' ? null : audience;
  const filterState = dispatchAudience ? (filterStates[dispatchAudience] ?? null) : null;
  const audienceFilterMeta = dispatchAudience ? (meta?.[dispatchAudience] ?? null) : null;
  const apiFilters = filterState ? toApiFilters(filterState, channel) : undefined;
  const filtersKey = JSON.stringify(apiFilters ?? null);
  const manualEmpty = filterState?.mode === 'manual' && filterState.selected.length === 0;
  /**
   * Everyone matching, with nothing carved out — the state "Select all" puts the
   * list in, and the one it is disabled in. Checked on both shapes because
   * either can express it: `all` with no exclusions is the query, and `manual`
   * never is, however many names it holds.
   */
  const allSelected = filterState?.mode === 'all' && filterState.excluded.length === 0;

  // Targeting mutations that change the POOL reset the picker's paging; picking
  // individuals must not, or the list would jump back to page 1 on every tick.
  const updateTargeting = (mutate: (state: AudienceFilterState) => AudienceFilterState) => {
    if (!dispatchAudience || !filterState) return;
    setFilterStates((current) => ({ ...current, [dispatchAudience]: mutate(filterState) }));
    setMembersPage(1);
  };
  const updatePicks = (mutate: (state: AudienceFilterState) => AudienceFilterState) => {
    if (!dispatchAudience || !filterState) return;
    setFilterStates((current) => ({ ...current, [dispatchAudience]: mutate(filterState) }));
    // In the 'selected' scope the picks ARE the query, so a toggle re-runs it and
    // paging has to start over — appending page 3 of a list that just got shorter
    // would show the wrong people.
    if (pickerScope === 'selected') setMembersPage(1);
  };

  /**
   * Is this audience being chosen from by hand? True while the list is open, and
   * true for a state that already CARRIES picks — reopening a draft that went to
   * 40 hand-picked people has to land on the list, not on "All matching" with 40
   * names stored invisibly underneath.
   */
  const picking =
    pickingOpen ||
    (!!filterState && (filterState.mode === 'manual' || filterState.excluded.length > 0));

  const openPicker = (scope: PickerScope) => {
    setPickerScope(scope);
    setPickingOpen(true);
    setMembers([]);
    setMembersTotal(0);
    // Cleared here, not only by the fetch: the 'selected' scope can legitimately
    // have nothing to fetch, and a spinner left running would read as a hang.
    setMembersLoading(false);
    setMembersPage(1);
    setMemberQuery('');
  };

  /**
   * Everyone matching, with nothing carved out — both shapes cleared, because
   * "All matching" has to mean the same thing whichever way the last selection
   * was stored. A chip that left four exclusions behind would read as the whole
   * list and send to four fewer people.
   */
  const selectEveryone = () =>
    updatePicks((state) => ({ ...state, mode: 'all', selected: [], excluded: [] }));

  /** Nobody. The empty end of the same axis — `manualEmpty` blocks Continue. */
  const selectNobody = () =>
    updatePicks((state) => ({ ...state, mode: 'manual', selected: [], excluded: [] }));

  // The filtered size is the server's call (suppression, dedup, the actual
  // filter SQL) — debounced so address typing doesn't stampede the API.
  useEffect(() => {
    // Reset with its own inputs — showing the previous audience's number
    // against the new selection would be worse than a beat of "…".
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilteredCount(null);
    setOptedOutCount(0);
    if (!dispatchAudience) return;
    const filters = JSON.parse(filtersKey) as CampaignAudienceFilters | null;
    if (!filters) return;
    // Manual picks: the count is the picks themselves; the server would only
    // confirm the intersection at send time. BOTH pairs are read, because the
    // one a campaign stores is decided by its channel — reading only the email
    // pair meant a hand-picked TEXT campaign fell through to the branch below
    // and was quoted the whole unfiltered pool as its recipient count, while
    // the sidebar beside it correctly said "1 hand-picked".
    const picked = filters.selectedPhones ?? filters.selectedEmails;
    if (picked) {
      setFilteredCount(picked.length);
      // `optedOutCount` stays 0 here, which renders as no figure rather than as
      // "none opted out" — the count is the picks themselves and no round trip
      // was made, so there is nothing to report. The picker's own rows carry
      // the answer per person, which is where a hand-picked list is read.
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      source.api
        .previewAudience({ audienceType: dispatchAudience, channel, audienceFilters: filters })
        .then(
          (preview) => {
            if (cancelled) return;
            setFilteredCount(preview.count);
            setOptedOutCount(preview.optedOutCount ?? 0);
          },
          () => {},
        );
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatchAudience, filtersKey, channel, source]);

  const recipientCount =
    audience === 'custom'
      ? customRows.length
      : manualEmpty
        ? 0
        : apiFilters
          ? filteredCount
          : (audienceCounts[audience] ?? null);

  /**
   * The opt-out figure for what is on screen, resolved down the same two paths
   * the count above is: the filtered preview when the audience is targeted, the
   * chip preview when it is not. A pasted list has no dispatch audience to ask
   * about, and a hand-picked one deliberately reports nothing — see the preview
   * effect for why.
   */
  const optedOutShown =
    audience === 'custom' ? 0 : apiFilters ? optedOutCount : (audienceOptedOut[audience] ?? 0);

  // The recipient picker. 'pool' pages the POOL filters (status/priority/address)
  // and never the individual picks — that is the list picks are made FROM. The
  // 'selected' scope sends the FULL filters instead, so the server resolves the
  // picks in SQL: this listing pages, and narrowing a page after the fact would
  // show a handful of picks on page one and none on page two.
  const poolKey = JSON.stringify((filterState ? toPoolFilters(filterState) : undefined) ?? null);
  const membersKey = pickerScope === 'selected' ? filtersKey : poolKey;
  // "Only people I pick" with nobody picked is an empty campaign, but it is not
  // an empty FILTER — toApiFilters omits an empty selection, so asking the server
  // would come back with the whole pool and claim it was the list.
  const selectedEmpty = pickerScope === 'selected' && manualEmpty;
  useEffect(() => {
    if (!picking || !dispatchAudience || selectedEmpty) return;
    let cancelled = false;
    // The spinner must flip with the keystroke that scheduled the fetch.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMembersLoading(true);
    const timer = setTimeout(() => {
      const filters = JSON.parse(membersKey) as CampaignAudienceFilters | null;
      source.api
        .listAudienceMembers({
          audienceType: dispatchAudience,
          channel,
          ...(filters ? { audienceFilters: filters } : {}),
          ...(pickerScope === 'selected' ? { scope: 'selected' as const } : {}),
          ...(memberQuery.trim() ? { q: memberQuery.trim() } : {}),
          page: membersPage,
        })
        .then(
          (result) => {
            if (cancelled) return;
            setMembers((current) => {
              if (membersPage === 1) return result.payload;
              const seen = new Set(current.map((row) => memberAddress(row)));
              return [...current, ...result.payload.filter((row) => !seen.has(memberAddress(row)))];
            });
            setMembersTotal(result.total);
            setMembersLoading(false);
          },
          () => {
            if (!cancelled) setMembersLoading(false);
          },
        );
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    picking,
    dispatchAudience,
    channel,
    pickerScope,
    selectedEmpty,
    membersKey,
    memberQuery,
    membersPage,
    source,
  ]);

  // Derived rather than fetched: with nothing picked there is nothing to ask for,
  // and the rows left over from the other scope are not this list.
  const visibleMembers = selectedEmpty ? [] : members;
  const visibleTotal = selectedEmpty ? 0 : membersTotal;
  const moreMembers = useInfiniteScroll({
    hasMore: visibleMembers.length < visibleTotal,
    busy: membersLoading,
    onLoadMore: () => setMembersPage((page) => page + 1),
    // This list scrolls inside a 260px box of its own, so a margin measured
    // against the viewport buys nothing: an ancestor's overflow has already
    // clipped the sentinel to nothing long before the page edge is in play.
    rootMargin: '0px',
  });

  const memberChecked = (email: string) =>
    filterState?.mode === 'manual'
      ? (filterState?.selected.includes(email) ?? false)
      : !(filterState?.excluded.includes(email) ?? false);

  const toggleMember = (email: string) =>
    updatePicks((state) => {
      const list = state.mode === 'manual' ? state.selected : state.excluded;
      const on = list.includes(email);
      const next = on ? list.filter((entry) => entry !== email) : [...list, email];
      return state.mode === 'manual' ? { ...state, selected: next } : { ...state, excluded: next };
    });

  const pickAudience = (key: CampaignAudience) => {
    setAudience(key);
    // Only the OPEN flag resets. The new audience's own picks decide whether the
    // list shows: switching to Affiliates and back must not quietly close a list
    // of 40 hand-picked customers and present it as "All matching".
    setPickingOpen(false);
    setPickerScope('pool');
    setMembers([]);
    setMembersTotal(0);
    setMembersPage(1);
    setMemberQuery('');
  };

  // No effect needed for the default: derive it until the user picks one.
  const effectiveInboxId = inboxId ?? senderInboxes[0]?.id ?? null;
  const selectedInbox = senderInboxes.find((inbox) => inbox.id === effectiveInboxId) ?? null;

  // Quota belongs to the mailbox, not the audience, so this preview is separate
  // from the audience-count chips above and re-runs whenever the mailbox, the
  // audience or its targeting changes. Only the review step needs it.
  const customKey = audience === 'custom' ? customText : '';
  /**
   * The quota probe is wanted from the THROTTLE step onward, not just at Review.
   *
   * That step is where the operator types a pace, and on a mailbox being warmed
   * up the pace is not what decides how long the send takes — so the answer has
   * to be in hand before they type rather than one screen after. A boolean
   * rather than `step` in the dependencies below: gating on `step >= 3` while
   * depending on `step` would re-ask for the same answer on every 3 → 4.
   */
  const wantsQuota = step >= 3;

  useEffect(() => {
    if (!wantsQuota || effectiveInboxId === null) {
      return;
    }
    const rows = parseCustomList(customKey, channel);
    // An empty custom list has nothing to weigh; the render derives null for it
    // rather than this effect writing state, which React 19 disallows here.
    if (audience === 'custom' && rows.length === 0) return;
    if (manualEmpty) return;
    const filters = JSON.parse(filtersKey) as CampaignAudienceFilters | null;
    let cancelled = false;
    source.api
      .previewAudience({
        audienceType: audience,
        channel,
        inboxId: effectiveInboxId,
        ...(audience === 'custom' ? { customRecipients: rows } : {}),
        ...(audience !== 'custom' && filters ? { audienceFilters: filters } : {}),
      })
      .then(
        (preview) => {
          if (cancelled) return;
          const options = preview.sendingOptions ?? null;
          setSendingOptions(options);
          // A bigger list or a different mailbox can make a previously affordable
          // credit send unaffordable — fall back rather than submitting a mode
          // the server will reject.
          // A bigger list or a different mailbox can make a previously
          // affordable credit send unaffordable — and a text campaign has no
          // credit option at all, so it always falls back here.
          if (!options?.canSendViaSes) setSendMode('spread');
        },
        () => {
          // A failed quota probe must not block sending; the server still
          // enforces the limit, and 'spread' is always valid.
          if (!cancelled) setSendingOptions(null);
        },
      );
    return () => {
      cancelled = true;
    };
  }, [wantsQuota, effectiveInboxId, audience, channel, customKey, filtersKey, manualEmpty, source]);

  // Derived, not stored: an empty custom list must not keep showing the options
  // computed for whatever audience was selected before it.
  const deliveryOptions =
    audience === 'custom' && customRows.length === 0 ? null : sendingOptions;
  const delivery = deliveryChoice(deliveryOptions);
  // Derived, never stored: when no choice is offered the send must be free, even
  // if the user picked credits for a larger audience a moment ago. Syncing state
  // instead would leave a window where a hidden picker still spends credits.
  const effectiveSendMode = resolveSendMode(deliveryOptions, sendMode);

  /**
   * How many days the mailbox's own allowance spreads this list over, or null
   * when it fits in one.
   *
   * The Throttle step asks for a per-MINUTE pace while the allowance is a
   * per-DAY ceiling, so on a mailbox being warmed up the two contradicted each
   * other on screen: the step quoted "~1,800 emails / hour" and Gmail's generic
   * caps at an operator whose mailbox is set to stop at twenty. Read off
   * `projectedDays`, which is already on the wire — the raw cap deliberately is
   * not, because it is a platform number the tenant cannot act on.
   */
  const spreadDays =
    deliveryOptions && !isSmsOptions(deliveryOptions) && !deliveryOptions.fitsToday
      ? deliveryOptions.projectedDays
      : null;

  const targetingLine = describeFilters(filterState, audienceFilterMeta?.vehicleTypes);
  // A dispatch audience the tenant's grants cannot read has no list to show —
  // the same reason the targeting block hides itself.
  const listBlocked =
    dispatchAudience && audienceFilterMeta && !audienceFilterMeta.available
      ? (audienceFilterMeta.reason ?? 'This list cannot be read on this tenant.')
      : null;

  /**
   * The pre-flight review's question to the server — the audience as the send
   * will resolve it, from the sender it will go out from. Null until both
   * exist, which the card renders as nothing to measure yet, not a failure.
   */
  const preflightRequest = useMemo<PreflightRequest | null>(() => {
    if (effectiveInboxId === null || noSender) return null;
    if (audience === 'custom' && customRows.length === 0) return null;
    if (manualEmpty) return null;
    const filters = JSON.parse(filtersKey) as CampaignAudienceFilters | null;
    return {
      audienceType: audience,
      channel,
      inboxId: effectiveInboxId,
      ...(audience === 'custom' ? { customRecipients: customRows } : {}),
      ...(audience !== 'custom' && filters ? { audienceFilters: filters } : {}),
      frequencyCapHours: frequencyCap,
      skipBounced,
    };
  }, [effectiveInboxId, noSender, audience, customRows, manualEmpty, filtersKey, channel, frequencyCap, skipBounced]);

  /**
   * The rows only the builder can answer: whether there is a sender at all,
   * whether the words are written and what a text costs, and the sending
   * settings. The server measures the sender's health and the list's history;
   * the AI reads the words — CampaignPreflight.tsx puts the three together.
   *
   * What each row OFFERS is as much the point as what it says: a check that
   * names a problem and leaves the operator to find the control is a check
   * they learn to scroll past.
   */
  const preflightRows = useMemo<PreflightRow[]>(() => {
    const rows: PreflightRow[] = [];
    const clock = (minutes: number) => minutesToClock(minutes);

    // Sender — before the server is asked anything about it.
    if (!selectedInbox) {
      rows.push({
        id: 'l:sender',
        group: 'sender',
        level: 'risk',
        title: noSender
          ? isSms
            ? 'No number to send from'
            : 'No mailbox to send from'
          : isSms
            ? 'Pick a number to send from'
            : 'Pick a mailbox to send from',
        detail: noSender
          ? `Connect ${isSms ? 'an SMS number' : 'a mailbox'} first — nothing can be checked or sent until then.`
          : 'Choose it in the Message step.',
        action: { kind: 'step', step: 2 },
      });
    } else if (!isSms) {
      const emailChannel = selectedInbox.channel as { verifiedForSending?: boolean } | undefined;
      if (emailChannel?.verifiedForSending !== true) {
        rows.push({
          id: 'l:verified',
          group: 'sender',
          level: 'warn',
          title: 'Not verified for sending',
          detail: 'The mailbox has not been confirmed as able to send — some sends may fail.',
          action: { kind: 'open_inbox' },
        });
      }
    }

    // The words — what the software can say without a model.
    if (isSms) {
      if (!bodySms.trim()) {
        rows.push({
          id: 'l:body',
          group: 'words',
          level: 'risk',
          title: 'No message yet',
          detail: 'Write it in the Message step.',
          action: { kind: 'step', step: 2 },
        });
      } else if (smsCost.segments > CAMPAIGN_MAX_SEGMENTS) {
        rows.push({
          id: 'l:segments',
          group: 'words',
          level: 'risk',
          title: 'Too long to send',
          detail: `${smsCost.segments} segments a text, and the limit is ${CAMPAIGN_MAX_SEGMENTS}. Send will refuse it until it is shorter.`,
          action: { kind: 'step', step: 2 },
        });
      } else if (smsCost.encoding === 'UCS-2') {
        const folded = toGsmSafe(bodySms);
        const foldedCost = segmentsFor(trackClicks && shortBase ? priceShortened(folded, shortBase) : folded);
        const fixable = foldedCost.encoding === 'GSM-7';
        const extra = (smsCost.segments - foldedCost.segments) * (recipientCount ?? 0);
        const blamed = smsCost.offenders
          .slice(0, 4)
          .map((character) => `“${character}”`)
          .join(' ');
        rows.push({
          id: 'l:unicode',
          group: 'words',
          level: 'warn',
          title: fixable ? `${blamed} ${smsCost.offenders.length === 1 ? 'doubles' : 'double'} the cost` : 'Sent as Unicode',
          detail: fixable
            ? `${smsCost.offenders.length === 1 ? 'It forces' : 'They force'} the whole text into Unicode: ${smsCost.segments} segment${
                smsCost.segments === 1 ? '' : 's'
              } each instead of ${foldedCost.segments}${extra > 0 ? ` — ${extra.toLocaleString()} extra across the list` : ''}. Fix it swaps in the plain keyboard version and changes no words.`
            : `${blamed} — an accented letter, a symbol or an emoji — puts the whole text in Unicode, which fits 70 characters a segment instead of 160. Reword it if the cost matters.`,
          ...(fixable ? { action: { kind: 'gsm_fix' as const } } : {}),
        });
      } else {
        rows.push({
          id: 'l:segments',
          group: 'words',
          level: 'ok',
          title: `${smsCost.segments} segment${smsCost.segments === 1 ? '' : 's'} a text`,
          detail: `${smsCost.characters} characters, all in the plain-text alphabet.`,
        });
      }
      if (trackClicks) {
        const own = shortLinkState.data?.domain?.verified ? shortLinkState.data.domain.host : null;
        rows.push(
          own
            ? {
                id: 'l:links',
                group: 'words',
                level: 'ok',
                title: 'Links on your own domain',
                detail: `Shortened to ${own}, which carriers trust more than a shared shortener.`,
              }
            : {
                id: 'l:links',
                group: 'words',
                level: 'note',
                title: 'Links shortened on the shared domain',
                detail: 'Carriers trust a link on your own domain more — set one up under Link domain.',
              },
        );
      }
      rows.push(
        unsubFooter
          ? {
              id: 'l:optout',
              group: 'words',
              level: 'ok',
              title: 'Ends with “Reply STOP to opt out.”',
              detail: 'Added to every text, and priced into the segments above.',
            }
          : {
              id: 'l:optout',
              group: 'words',
              level: 'warn',
              title: 'Doesn’t say how to opt out',
              detail: 'STOP works either way, but carriers expect a marketing text to say so — leaving it off is a common reason texts are filtered.',
              action: { kind: 'unsubscribe_on' },
            },
      );
    } else {
      if (!subject.trim()) {
        rows.push({
          id: 'l:subject',
          group: 'words',
          level: 'risk',
          title: 'No subject yet',
          detail: 'Write one in the Message step.',
          action: { kind: 'step', step: 2 },
        });
      }
      if (!body.text.trim()) {
        rows.push({
          id: 'l:body',
          group: 'words',
          level: 'risk',
          title: 'No body yet',
          detail: 'Write it in the Message step.',
          action: { kind: 'step', step: 2 },
        });
      }
    }

    // Sending.
    if (!isSms) {
      rows.push(
        unsubFooter
          ? {
              id: 'l:unsub',
              group: 'sending',
              level: 'ok',
              title: 'Unsubscribe link in the footer',
              detail: `Beside the one-click List-Unsubscribe header Gmail and Yahoo ask for${
                unsubLinkText.trim() ? ` · the link reads “${unsubLinkText.trim()}”` : ''
              }.`,
            }
          : {
              id: 'l:unsub',
              group: 'sending',
              level: 'warn',
              title: 'No unsubscribe link in the footer',
              detail: 'The List-Unsubscribe header still rides on every email, but a link people can see is what stops them reporting it as spam.',
              action: { kind: 'unsubscribe_on' },
            },
      );
    }
    if (quietOn && windowStart === windowEnd) {
      rows.push({
        id: 'l:window',
        group: 'sending',
        level: 'risk',
        title: 'The sending hours start and end at the same time',
        detail: 'Set a real window, or let it send at any time.',
        action: { kind: 'step', step: 3 },
      });
    } else if (quietOn) {
      rows.push({
        id: 'l:window',
        group: 'sending',
        level: 'ok',
        title: `Only ${clock(windowStart)}–${clock(windowEnd)}, company time`,
        detail: 'It pauses outside those hours and carries on in the next window.',
      });
    } else if (isSms) {
      rows.push({
        id: 'l:window',
        group: 'sending',
        level: 'warn',
        title: 'No sending hours set',
        detail: 'US rules (TCPA) expect marketing texts only between 8am and 9pm in the recipient’s time.',
        action: { kind: 'set_window' },
      });
    } else {
      rows.push({
        id: 'l:window',
        group: 'sending',
        level: 'ok',
        title: 'Sends at any time of day',
        detail: 'Starts as soon as it is sent, at the pace below.',
      });
    }
    if (!isSms) {
      rows.push({
        id: 'l:clicks',
        group: 'sending',
        level: 'ok',
        title: trackClicks ? 'Clicks counted per link' : 'Clicks not counted',
        detail: !trackLinks
          ? 'Visits to your website are not tagged — links go out as written.'
          : !trackingName
            ? 'No tracking name, so links go out as written.'
            : linkSummary.tagged
              ? `${linkSummary.tagged} link${linkSummary.tagged === 1 ? '' : 's'} to ${linkSummary.taggedHosts.join(', ')} carry “${trackingName}” for your website analytics.`
              : `“${trackingName}” is set, but no link in this email points at your website.`,
      });
    }
    rows.push(
      isSms
        ? {
            id: 'l:pace',
            group: 'sending',
            level: 'ok',
            title: `Up to ${throttle} a minute`,
            detail: 'And less while agents are using this number — they always come first.',
          }
        : throttle > 30
          ? {
              id: 'l:pace',
              group: 'sending',
              level: 'warn',
              title: `${throttle} a minute is fast for a mailbox`,
              detail: `About ${(throttle * 60).toLocaleString()} an hour — high for a mailbox that has not sent bulk before. 30 a minute or less is safer.`,
              action: { kind: 'step', step: 3 },
            }
          : {
              id: 'l:pace',
              group: 'sending',
              level: 'ok',
              title: `${throttle} a minute`,
              detail: `About ${(throttle * 60).toLocaleString()} an hour.`,
            },
    );
    return rows;
  }, [
    selectedInbox,
    noSender,
    isSms,
    bodySms,
    smsCost,
    trackClicks,
    shortBase,
    shortLinkState.data,
    recipientCount,
    unsubFooter,
    unsubLinkText,
    subject,
    body.text,
    quietOn,
    windowStart,
    windowEnd,
    trackLinks,
    trackingName,
    linkSummary,
    throttle,
  ]);

  const preflightHeads: Record<PreflightGroup, string> = {
    sender: selectedInbox ? identityLine(selectedInbox) : 'Nothing picked yet',
    audience: `${audienceLabel(audience)}${targetingLine ? ` · ${targetingLine}` : ''}${
      recipientCount !== null ? ` · ${recipientCount.toLocaleString()}` : ''
    }`,
    words: isSms
      ? `${smsCost.segments} segment${smsCost.segments === 1 ? '' : 's'}`
      : subject.trim()
        ? `“${subject.trim()}”`
        : 'No subject yet',
    sending: `${quietOn ? `${minutesToClock(windowStart)}–${minutesToClock(windowEnd)}` : 'Any time'} · ${throttle} a minute${
      frequencyCap
        ? ` · skips anyone contacted in the last ${FREQUENCY_CAPS.find((cap) => cap.hours === frequencyCap)?.label ?? `${frequencyCap} hours`}`
        : ''
    }`,
  };

  /**
   * Where "Use this" puts a suggestion: the words the AI quoted, replaced where
   * they are — the subject first, then the body — and only when they occur
   * exactly once, so it can never land on the wrong match.
   */
  const escapeHtml = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const placeSuggestion = (evidence: string, suggestion: string, apply: boolean): boolean => {
    if (isSms) {
      const next = replaceOnce(bodySms, evidence, suggestion);
      if (next !== null && apply) setBodySms(next);
      return next !== null;
    }
    const inSubject = replaceOnce(subject, evidence, suggestion);
    if (inSubject !== null) {
      if (apply) setSubject(inSubject);
      return true;
    }
    const inHtml = replaceOnce(body.html, evidence, escapeHtml(suggestion));
    if (inHtml !== null && apply) setBody({ html: inHtml, text: htmlToText(inHtml) });
    return inHtml !== null;
  };

  const onPreflightAction = (action: PreflightAction) => {
    switch (action.kind) {
      case 'skip_bounced':
        setSkipBounced(action.on);
        break;
      case 'frequency_cap':
        setFrequencyCap(action.hours);
        break;
      case 'set_window':
        setQuietChoice(true);
        setWindowStart(8 * 60);
        setWindowEnd(21 * 60);
        break;
      case 'unsubscribe_on':
        setUnsubFooter(true);
        break;
      case 'gsm_fix':
        setBodySms((current) => toGsmSafe(current));
        break;
      case 'open_inbox':
        if (selectedInbox) openSettingsTab(`/inboxes/${selectedInbox.id}`, source.embedded);
        break;
      case 'use_suggestion':
        placeSuggestion(action.evidence, action.suggestion, true);
        break;
      default:
        break;
    }
  };

  /**
   * What still stops this being SAVED, or null. Saving asks for less than sending
   * on purpose: a draft is work in progress, and refusing to store one until the
   * copy is written is what loses the audience somebody just spent ten minutes
   * picking. The server draws the same line — `POST /campaigns` takes an empty
   * subject and body, `POST /:id/send` refuses one.
   */
  const saveBlocker =
    selectedInbox === null
      ? noSender
        ? isSms
          ? 'Connect a sending number'
          : 'Connect a sending mailbox'
        : isSms
          ? 'Pick a sending number in the Message step'
          : 'Pick a sending mailbox in the Message step'
      : !name.trim()
        ? 'Name the campaign in the Message step'
        : manualEmpty
          ? 'Tick at least one recipient'
          : audience === 'custom' && customRows.length === 0
            ? 'Paste or upload at least one recipient'
            : null;

  /**
   * What stops it being SENT — everything above, plus the mail itself.
   *
   * A mailbox that lost its authorization stops the send but NOT the save: the
   * copy and the audience are still worth keeping, and the campaign becomes
   * sendable the moment somebody reconnects the channel. Blocking here rather
   * than letting it through is the server's own arithmetic — an auth failure is
   * fatal to a whole campaign at once (services/campaign), so the alternative is
   * a run that fails on recipient one and has to be explained afterwards.
   */
  const sendBlocker =
    saveBlocker ??
    (selectedInbox && needsReauth(selectedInbox)
      ? `${identityLine(selectedInbox)} needs reconnecting before it can send`
      : isSms
        ? !bodySms.trim()
          ? 'Write the text message in the Message step'
          : // The same ceiling the server enforces, said here so it is a greyed
            // button with a reason rather than a red box after the press.
            smsCost.segments > CAMPAIGN_MAX_SEGMENTS
            ? `This message is ${smsCost.segments} segments and the limit is ${CAMPAIGN_MAX_SEGMENTS} — shorten it`
            : null
        : !subject.trim()
          ? 'Write a subject in the Message step'
          : !body.text.trim()
            ? 'Write the email body in the Message step'
            : null);

  const readyToSave = saveBlocker === null;
  const readyToSend = sendBlocker === null;
  /**
   * The time itself, checked against the same bounds the server holds — so a bad
   * choice is a greyed button with a reason under it rather than a red box after
   * the press. `mayApprove` is in here because scheduling IS sending: a `draft`
   * author submits for review and the reviewer picks the time.
   */
  const timeBlocker = whenMode === 'later' ? scheduleBlocker(scheduleWall, zone) : null;
  const scheduleBlocked = !mayApprove
    ? 'Only someone who can send a campaign can schedule one'
    : (sendBlocker ?? timeBlocker);
  const readyToSchedule = scheduleBlocked === null;

  // The nullable fields go out as explicit nulls, never omitted: PATCH keeps
  // whatever the body leaves out, so an omitted one would make clearing a
  // from-name or removing every filter a silent no-op on a reopened draft.
  const buildDraft = (): CampaignDraft => ({
    name: name.trim(),
    // Only ever sent on a CREATE — the server refuses a change and a PATCH that
    // repeated it would 1001 every save of an existing draft.
    ...(draft ? {} : { channel }),
    inboxId: selectedInbox?.id ?? 0,
    // A text campaign sends nulls rather than empty strings in the email
    // fields, matching what the server stores: '' is a value every later
    // reader has to keep deciding is not really there.
    subject: isSms ? null : subject.trim(),
    bodyHtml: isSms ? null : body.html,
    bodyText: isSms ? null : body.text.trim() ? body.text : null,
    bodySms: isSms ? bodySms : null,
    fromName: isSms ? null : fromName.trim() || null,
    audienceType: audience,
    ...(audience === 'custom' ? { customRecipients: customRows } : {}),
    audienceFilters: audience === 'custom' ? null : (apiFilters ?? null),
    throttlePerMinute: throttle,
    includeUnsubscribe: unsubFooter,
    contactOptedOut,
    unsubscribeFooterText: unsubText.trim() || null,
    unsubscribeLinkText: unsubLinkText.trim() || null,
    // An explicit null when off, for the from-name's reason: PATCH keeps what a
    // body leaves out, so omitting it could never switch tracking off.
    utmCampaign: !isSms && trackLinks ? trackingName || null : null,
    trackClicks,
    sendWindowStart: quietOn ? windowStart : null,
    sendWindowEnd: quietOn ? windowEnd : null,
    frequencyCapHours: frequencyCap,
    skipBounced,
  });

  /**
   * A draft in hand is edited in place; otherwise this is a new campaign. A test
   * send saves a NEW campaign before the operator has finished, so the id it was
   * given is remembered — or the real send afterwards would create a second one.
   */
  const [savedId, setSavedId] = useState<number | null>(draft?.id ?? null);
  const persist = async () => {
    const saved = savedId
      ? await source.api.updateCampaign(savedId, buildDraft())
      : await source.api.createCampaign(buildDraft());
    setSavedId(saved.id);
    return saved;
  };

  /**
   * Save, then send this exact copy to the person pressing the button (or the
   * address they typed). Saving first for `schedule`'s reason: the test has to
   * be of what is on screen, not of the last save.
   */
  const sendTest = async () => {
    if (!readyToSave || testState.busy) return;
    setTestState({ busy: true, note: null, error: false });
    try {
      const campaign = await persist();
      const result = await source.api.sendCampaignTest(campaign.id, testTo.trim() || undefined);
      setTestState({ busy: false, note: `Test sent to ${result.to}.`, error: false });
    } catch (e) {
      setTestState({
        busy: false,
        note: e instanceof ApiError ? e.message : 'Could not send the test.',
        error: true,
      });
    }
  };

  const saveDraft = async () => {
    if (!readyToSave) return;
    setBusy('draft');
    setError(null);
    try {
      const campaign = await persist();
      onSavedDraft(campaign);
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'forbidden') noteSendDenied();
      setError(
        e instanceof ApiError ? e.message : `Could not save ${draft ? 'your changes' : 'the campaign'}.`,
      );
    } finally {
      setBusy(null);
    }
  };

  /**
   * Send it, or submit it for review — one action, because the server decides
   * which one happened from the caller's campaign level. Routing them separately
   * in the client would let the client pick, and the button would then be able
   * to promise something the server refuses.
   */
  const launch = async () => {
    if (!readyToSend) return;
    setBusy('send');
    setError(null);
    try {
      const campaign = await persist();
      await source.api.sendCampaign(campaign.id, effectiveSendMode);
      onLaunched(campaign);
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'forbidden') noteSendDenied();
      setError(
        e instanceof ApiError
          ? e.message
          : mayApprove
            ? 'Could not start the campaign.'
            : 'Could not send it for review.',
      );
      setBusy(null);
    }
  };

  /**
   * Book it instead of pressing it.
   *
   * The draft is saved first, exactly as `launch` does, because the schedule is a
   * decision about THIS copy and this audience — scheduling a campaign whose
   * unsaved subject only exists in the browser would book last save's mail.
   *
   * Landing back on the list rather than on Results is deliberate: there are no
   * results, and a campaign that has not gone out yet belongs with the ones that
   * have not gone out yet.
   */
  const schedule = async () => {
    if (!readyToSchedule) return;
    const at = instantFromWall(scheduleWall, zone);
    if (!at) return;
    setBusy('schedule');
    setError(null);
    try {
      const campaign = await persist();
      const scheduled = await source.api.scheduleCampaign(campaign.id, {
        scheduledAt: at.toISOString(),
        sendMode: effectiveSendMode,
      });
      onSavedDraft(scheduled);
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'forbidden') noteSendDenied();
      setError(e instanceof ApiError ? e.message : 'Could not schedule the campaign.');
      setBusy(null);
    }
  };

  /** Take the time off. It goes back to being an ordinary draft, on this screen. */
  const unschedule = async () => {
    if (!draft) return;
    setBusy('unschedule');
    setError(null);
    try {
      await source.api.unscheduleCampaign(draft.id);
      onSavedDraft({ ...draft, status: 'draft' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not call off the schedule.');
      setBusy(null);
    }
  };

  const runAi = async (mode: 'generate' | 'improve') => {
    const brief = aiPrompt.trim();
    if (mode === 'generate' && !brief) return;
    // Generate REPLACES, so it asks — but only when there is something to lose.
    // The question names what this channel actually holds: "subject and body"
    // over a text box is a prompt about fields that are not on screen.
    const hasCopy = isSms ? bodySms.trim() : body.text.trim();
    if (
      mode === 'generate' &&
      hasCopy &&
      !window.confirm(
        isSms
          ? 'Replace the current message with a fresh AI draft?'
          : 'Replace the current subject and body with a fresh AI draft?',
      )
    ) {
      return;
    }
    setAiBusy(mode);
    setAiError(null);
    try {
      const steer = {
        mode,
        ...(mode === 'generate' ? { prompt: brief } : {}),
        ...(mode === 'improve' && brief ? { instruction: brief } : {}),
        audienceType: audience,
        ...(fromName.trim() ? { fromName: fromName.trim() } : {}),
      } as const;
      if (isSms) {
        const result = await source.api.campaignSmsCopy({
          ...steer,
          ...(bodySms.trim() ? { bodySms } : {}),
        });
        setBodySms(result.bodySms);
      } else {
        const result = await source.api.campaignCopy({
          ...steer,
          ...(subject.trim() ? { subject: subject.trim() } : {}),
          ...(body.html.trim() ? { bodyHtml: body.html } : {}),
        });
        setSubject(result.subject);
        setBody({ html: result.bodyHtml, text: htmlToText(result.bodyHtml) });
      }
      setAiPrompt('');
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'forbidden') noteSendDenied();
      setAiError(e instanceof ApiError ? e.message : 'The AI assistant is unavailable right now.');
    } finally {
      setAiBusy(null);
    }
  };

  const pickFile = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    const text = await file.text();
    pickAudience('custom');
    setCustomEdited(true);
    setCustomText(text);
    if (fileInput.current) fileInput.current.value = '';
  };

  /**
   * "Try a test campaign" (campaignTour.ts): the builder walked once, for real,
   * to a list of one. Each moment moves the builder to its step and rings one
   * control; the operator does the actual work in the actual fields, so what
   * they finish with is a campaign that goes out — to them.
   */
  const tourAddress = isSms ? (capabilities?.phone ?? null) : (capabilities?.email ?? null);
  const tourMoments = useMemo(
    () =>
      campaignTourMoments({
        channel,
        address: tourAddress,
        aiReady,
        mayManageAi,
        mayApprove,
        aiOffered,
        hasSender: !noSender,
        mayConnect,
      }),
    [channel, tourAddress, aiReady, mayManageAi, mayApprove, aiOffered, noSender, mayConnect],
  );
  const [tourAt, setTourAt] = useState<number | null>(tour ? 0 : null);
  const tourMoment = tourAt === null ? null : tourMoments[Math.min(tourAt, tourMoments.length - 1)];
  const tourTo = (index: number) => {
    const next = tourMoments[Math.max(0, Math.min(index, tourMoments.length - 1))];
    setTourAt(Math.max(0, Math.min(index, tourMoments.length - 1)));
    setStep(next.step);
  };
  const endTour = () => {
    setTourAt(null);
    onTourEnd?.();
  };
  const tourAction = () => {
    const action = tourMoment?.action;
    if (!action || tourAt === null) return null;
    const run = () => {
      if (action.kind === 'useMyAddress' && tourAddress) {
        // The list a test is: you, under your own name, so {{name}} reads right.
        pickAudience('custom');
        setCustomEdited(true);
        setCustomText(`${tourAddress}${capabilities?.name ? `, ${capabilities.name}` : ''}`);
        tourTo(tourAt + 1);
      } else if (action.kind === 'nameIt') {
        setName('Test campaign');
      } else if (action.kind === 'setUpAi') {
        openAiSettings();
      } else if (action.kind === 'connect') {
        openSettingsTab('/connect', source.embedded);
      }
    };
    const blocked = action.kind === 'useMyAddress' && !tourAddress;
    return (
      <>
        <button
          type="button"
          className="tour-btn"
          disabled={blocked}
          title={blocked ? action.unavailable : undefined}
          onClick={run}
        >
          {action.label}
          {action.kind === 'setUpAi' || action.kind === 'connect' ? <ArrowUpRight size={13} /> : null}
        </button>
        {action.kind === 'setUpAi' || action.kind === 'connect' ? (
          <button
            type="button"
            className="tour-btn"
            onClick={() => (action.kind === 'connect' ? reloadInboxes() : setAiProbe((n) => n + 1))}
          >
            Check again
          </button>
        ) : null}
      </>
    );
  };
  const tourText = (text: string, key: number) => (
    <p key={key}>
      {boldSegments(text).map((part, index) => (part.bold ? <b key={index}>{part.text}</b> : part.text))}
    </p>
  );

  /**
   * One line per step for the rail — what each step has decided so far, so the
   * whole campaign reads down the left edge without visiting each step.
   */
  /**
   * Whether each step has what a send needs — what the rail's tick means. Not
   * "visited": a Message step walked past with nothing written is the one a
   * tick must not claim is finished.
   */
  const stepReady = [
    recipientCount !== null && recipientCount > 0,
    Boolean(name.trim()) && selectedInbox !== null && (isSms ? Boolean(bodySms.trim()) : Boolean(subject.trim() && body.text.trim())),
    // Defaults are already a complete answer here, so it is ready once seen.
    step > 3,
    false,
  ];

  const stepSummaries = [
    `${isSms ? 'Text' : 'Email'} · ${audienceLabel(audience)}${targetingLine ? ` · ${targetingLine}` : ''}`,
    isSms
      ? bodySms.trim()
        ? `${smsCost.segments} segment${smsCost.segments === 1 ? '' : 's'} a text`
        : 'Not written yet'
      : subject.trim()
        ? `“${subject.trim()}”`
        : body.text.trim()
          ? 'No subject yet'
          : 'Not written yet',
    `${quietOn ? `${minutesToClock(windowStart)}–${minutesToClock(windowEnd)}` : 'Any time'} · ${throttle} a minute`,
    draft?.status === 'scheduled' && draft.scheduledAt
      ? `Scheduled ${fmtDateTime(draft.scheduledAt)}`
      : whenMode === 'later'
        ? 'At a set time'
        : 'Checks, a test, then send',
  ];

  /** Minutes as the rail and the context panel say them: "about 41 minutes", "about 3 hours". */
  const roughly = (minutes: number) =>
    minutes < 2
      ? 'about a minute'
      : minutes < 90
        ? `about ${Math.round(minutes)} minutes`
        : `about ${Math.round(minutes / 60)} hours`;

  // The message as a recipient sees it — beside the editor, in the context
  // panel, so it is in view while the words are being written.
  const previewPanel = (
          <div className="cmg-preview-wrap">
            {isSms ? (
              <>
                <div className="cmg-preview-mail">
                  <div className="cmg-preview-mail-head">
                    <span>
                      {selectedInbox ? identityLine(selectedInbox) : 'no number selected'}
                    </span>
                    {/**
                     * The cost of the MERGED copy, not the copy as typed.
                     * `{{company_name}}` is fourteen characters in the box
                     * and can be thirty on the wire, which is exactly the
                     * kind of surprise that turns a one-segment blast into a
                     * two-segment bill — so the preview prices what actually
                     * goes out.
                     */}
                    <span className="cmg-preview-subject">
                      {(() => {
                        const merged = previewMerge(bodySms, undefined, tenantMerge);
                        const cost = segmentsFor(merged);
                        return `${cost.segments} segment${cost.segments === 1 ? '' : 's'}${
                          cost.encoding === 'UCS-2' ? ' · Unicode' : ''
                        }`;
                      })()}
                    </span>
                  </div>
                  {/* No html and no frame: a text is text, and the line
                      breaks the operator typed are the whole layout. */}
                  <div
                    className="cmg-preview-body"
                    style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}
                  >
                    {previewMerge(bodySms, undefined, tenantMerge) || (
                      <span style={{ color: 'var(--c-muted)' }}>
                        The message appears here as you type.
                      </span>
                    )}
                  </div>
                </div>
                <div className="cmg-hint">
                  Preview substitutes a sample recipient and your real company details. Include
                  a way to opt out — most carriers expect it, and a reply of STOP is honoured
                  automatically either way.
                </div>
              </>
            ) : (
              <>
                <div className="cmg-preview-mail">
                  <div className="cmg-preview-mail-head">
                    <span>
                      {fromName.trim() || 'LimoSphere'} ·{' '}
                      {selectedInbox ? identityLine(selectedInbox) : 'no mailbox selected'}
                    </span>
                    <span className="cmg-preview-subject">
                      {previewMerge(subject, undefined, tenantMerge) || 'Subject preview'}
                    </span>
                  </div>
                  <div
                    className="cmg-preview-body"
                    // The author's own draft, previewed back to them — same trust
                    // model as any email editor.
                    dangerouslySetInnerHTML={{
                      __html:
                        previewMerge(body.html, undefined, tenantMerge) ||
                        '<span style="color:var(--c-muted)">Body preview appears here as you type.</span>',
                    }}
                  />
                </div>
                <div className="cmg-hint">
                  Preview substitutes a sample recipient and your real company details. Every
                  mail also carries an open-tracking pixel and a List-Unsubscribe header.
                </div>
              </>
            )}
          </div>
  );

  return (
    <div className="cmg-builder">
      {/* The campaign itself, and the way forward. The step rail below is the
          way to anywhere else. */}
      <div className="cmg-builder-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cmg-builder-title">
            {name.trim() || (draft ? 'Untitled campaign' : 'New campaign')}
          </div>
          <div className="cmg-results-sub">
            {isSms ? 'Text message' : 'Email'} ·{' '}
            {draft?.status === 'scheduled' ? 'Scheduled' : 'Draft'}
            {savedId ? '' : ' · not saved yet'}
          </div>
        </div>
        {step > 1 && (
          <button type="button" className="cbtn" onClick={() => setStep(Math.max(1, step - 1))}>
            Back
          </button>
        )}
        {step < STEPS.length && (
          <button
            type="button"
            className="cbtn cbtn-primary"
            onClick={() => setStep(Math.min(STEPS.length, step + 1))}
          >
            Next: {STEPS[step]}
            <ArrowRight size={13} />
          </button>
        )}
      </div>

      {error && <div className="comms-error-note">{error}</div>}

      {noSender && (
        <div className="cmg-nosender" data-tour="no-sender">
          <CircleAlert size={16} />
          <div className="cmg-nosender-text">
            <b>{isSms ? 'No number to send texts from yet' : 'No mailbox to send from yet'}</b>
            <span>
              {isSms
                ? 'A text campaign goes out from a Twilio or RingCentral number connected here — a WhatsApp sender cannot send one.'
                : 'A campaign goes out from a mailbox connected here — Gmail, Microsoft 365 or any SMTP mailbox.'}{' '}
              {mayConnect
                ? 'Connect one and it appears in the Message step. Until then this campaign can be built but not saved or sent.'
                : 'Connecting one needs an administrator — ask them to connect one, or to give you access to one that is. Until then this campaign can be built but not saved or sent.'}
            </span>
          </div>
          <div className="cmg-nosender-actions">
            {mayConnect && (
              <button
                type="button"
                className="cbtn cbtn-primary"
                onClick={() => openSettingsTab('/connect', source.embedded)}
              >
                <Plus size={13} />
                {isSms ? 'Connect a number' : 'Connect a mailbox'}
                <ArrowUpRight size={13} />
              </button>
            )}
            {/* The channel is still open on a new campaign, and the other one may
                be ready to go — a texts-only company should not be stuck here. */}
            {!draft && otherChannelHasSender && (
              <button type="button" className="cbtn" onClick={() => switchChannel(isSms ? 'email' : 'sms')}>
                {isSms ? 'Send an email instead' : 'Send a text instead'}
              </button>
            )}
            <button type="button" className="cbtn" onClick={reloadInboxes}>
              Check again
            </button>
          </div>
        </div>
      )}

      {tourMoment && tourAt !== null && (
        <Spotlight
          target={tourMoment.target}
          title={tourMoment.title}
          index={Math.min(tourAt, tourMoments.length - 1)}
          count={tourMoments.length}
          actions={tourAction()}
          missing={tourMoment.missing ? tourText(tourMoment.missing, 0) : undefined}
          onBack={() => tourTo(tourAt - 1)}
          onNext={() => (tourAt >= tourMoments.length - 1 ? endTour() : tourTo(tourAt + 1))}
          onClose={endTour}
        >
          {tourMoment.body.map(tourText)}
          {tourMoment.action?.unavailable && tourMoment.action.kind === 'useMyAddress' && !tourAddress ? (
            <p>{tourMoment.action.unavailable}</p>
          ) : null}
        </Spotlight>
      )}

      {/* A scheduled send that never left. It reopens here rather than in Results
          because there are no results — nobody was resolved onto it and nothing
          was charged — so this is the only place the reason can be read, and it
          has to be read before the time is picked again. */}
      {draft?.status === 'failed' && !draft.startedAt && draft.error && (
        <div className="comms-error-note">
          This campaign did not go out at its scheduled time. {draft.error} Fix it and pick a new
          time, or send it now.
        </div>
      )}

      <div className="cmg-builder-grid">
        {/* Three zones: the steps down the left, each with what it has
            decided so far; the work for this step in the middle; and what
            this step is FOR on the right — who matches, what the recipient
            sees, how it will go, the summary. The two outer zones sit on the
            secondary surface so the white middle is unmistakably where the
            editing happens. */}
        <nav className="cmg-steprail" aria-label="Campaign steps">
          {STEPS.map((label, index) => {
            const n = index + 1;
            const active = step === n;
            const done = !active && stepReady[index];
            return (
              <button
                key={label}
                type="button"
                className={`cmg-step${active ? ' active' : ''}${done ? ' done' : ''}`}
                aria-current={active ? 'step' : undefined}
                onClick={() => setStep(n)}
              >
                <span className="cmg-step-circle">{done ? <Check size={13} /> : n}</span>
                <span className="cmg-step-text">
                  <span className="cmg-step-label">{label}</span>
                  <span className="cmg-step-sum">{stepSummaries[index]}</span>
                </span>
              </button>
            );
          })}
          <div className="cmg-receive" data-card="will-receive">
            <span className="cmg-receive-label">Will receive it</span>
            <span className="cmg-receive-count">
              {recipientCount === null ? '—' : recipientCount.toLocaleString()}
            </span>
            <span className="cmg-receive-sub">
              {audience === 'custom'
                ? 'on your list'
                : `${audienceLabel(audience).toLowerCase()}, if it went now`}
            </span>
          </div>
          {/* The list is reachable from every step here, not only from the
              filter row on step 1 — "who actually gets this" is the question
              the count raises, so it is where it should be answerable. */}
          <button
            type="button"
            className="cbtn"
            disabled={listBlocked !== null}
            title={
              listBlocked ??
              (audience === 'custom' ? 'Show the list you pasted' : 'Show exactly who receives this')
            }
            onClick={() => {
              setStep(1);
              if (dispatchAudience) openPicker('selected');
            }}
          >
            <ListFilter size={13} />
            See the list
          </button>
          <button
            type="button"
            className="cbtn"
            disabled={!readyToSave || busy !== null}
            title={saveBlocker ?? undefined}
            onClick={() => void saveDraft()}
          >
            {busy === 'draft' ? 'Saving…' : draft ? 'Save changes' : 'Save as draft'}
          </button>
          {/* A disabled button with no reason reads as a broken one — and the
              reason is always in a step that is not on screen. */}
          <div className="cmg-hint">
            {saveBlocker
              ? `${saveBlocker} to save this draft.`
              : 'Saves your audience, targeting and the mail exactly as designed — finish it later from All campaigns.'}
          </div>
          {draft?.status === 'scheduled' && (
            <>
              <button
                type="button"
                className="cbtn"
                disabled={busy !== null}
                title="Take the time off — it goes back to being a draft"
                onClick={() => void unschedule()}
              >
                {busy === 'unschedule' ? <span className="btn-spin" /> : <CalendarOff size={13} />}
                {busy === 'unschedule' ? 'Calling it off…' : 'Cancel schedule'}
              </button>
              <div className="cmg-hint">
                Nothing has been sent and nobody has been resolved onto it yet — cancelling leaves an
                ordinary draft.
              </div>
            </>
          )}
        </nav>

        <div className="cmg-work">
          {step === 1 && (
            <div className="cmg-card">
              {/**
               * The channel comes FIRST because everything after it changes:
               * the audience is read out of a different column, the picks are a
               * different list and the message is a different field. It is
               * fixed on the first save — a campaign whose channel could move
               * under an audience already picked by email is one that texts the
               * wrong people — so an existing draft shows it and cannot change
               * it.
               */}
              <div className="cmg-work-head">
                <h2>Who is it for?</h2>
                <p>Pick the list, then narrow it down — the panel beside you counts who matches as you go.</p>
              </div>
              <div className="cmg-card-title">How it goes out</div>
              <div className="cmg-aud-chips" data-tour="channel">
                {[
                  {
                    key: 'email' as const,
                    label: 'Email',
                    icon: Mail,
                    hint: 'A subject, formatting and links',
                  },
                  {
                    key: 'sms' as const,
                    label: 'Text message',
                    icon: MessageSquare,
                    hint: 'Short, from one of your numbers',
                  },
                ].map(({ key, label, icon: Icon, hint }) => (
                  <button
                    key={key}
                    type="button"
                    className={`cmg-aud-chip${channel === key ? ' on' : ''}`}
                    disabled={!!draft && draft.channel !== key}
                    title={draft ? 'A campaign cannot change channel — start a new one instead' : hint}
                    onClick={() => switchChannel(key)}
                  >
                    <Icon size={14} />
                    {label}
                    {channel === key ? <Check size={12} /> : <Plus size={12} />}
                  </button>
                ))}
              </div>
              {isSms && !draft && (
                <div className="cmg-hint">
                  Only people whose dispatch record has a phone number are reachable, and anyone
                  who has replied STOP is skipped automatically.
                </div>
              )}
              <div className="cmg-card-title">Who receives this</div>
              <div className="cmg-aud-chips">
                {AUDIENCES.map(({ key, label, icon: Icon }) => {
                  const on = audience === key;
                  const count = key === 'custom' ? customRows.length || null : audienceCounts[key];
                  const loadError = key === 'custom' ? undefined : audienceErrors[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`cmg-aud-chip${on ? ' on' : ''}`}
                      title={loadError}
                      data-tour={key === 'custom' ? 'audience-custom' : undefined}
                      onClick={() => pickAudience(key)}
                    >
                      <Icon size={14} />
                      {label}
                      {count !== null && count !== undefined && (
                        <span className="cmg-aud-count">{count.toLocaleString()}</span>
                      )}
                      {loadError && <TriangleAlert size={12} className="cmg-aud-alert" />}
                      {on ? <Check size={12} /> : <Plus size={12} />}
                    </button>
                  );
                })}
              </div>
              {dispatchAudience && audienceErrors[dispatchAudience] && (
                <div className="cmg-warnbox">
                  <TriangleAlert size={15} />
                  <span>{audienceErrors[dispatchAudience]}</span>
                </div>
              )}

              {dispatchAudience && audienceFilterMeta && !audienceFilterMeta.available && (
                <div className="cmg-warnbox">
                  <TriangleAlert size={15} />
                  <span>{audienceFilterMeta.reason}</span>
                </div>
              )}

              {dispatchAudience && filterState && audienceFilterMeta?.available && (
                <div className="cmg-filters">
                  <div className="cmg-filters-head">
                    <ListFilter size={14} />
                    <span>Target within {audienceLabel(audience).toLowerCase()}</span>
                    <span className="cmg-filters-count">
                      {manualEmpty
                        ? 'no one picked yet'
                        : recipientCount === null
                          ? 'counting…'
                          : `${recipientCount.toLocaleString()} recipient${recipientCount === 1 ? '' : 's'}`}
                    </span>
                  </div>
                  {audienceFilterMeta.statuses.length > 0 && (
                    <div className="cmg-filter-row">
                      <span className="cmg-field-label">Status</span>
                      <div className="cmg-filter-chips">
                        {audienceFilterMeta.statuses.map((status) => {
                          const on = filterState.statuses.includes(status);
                          return (
                            <button
                              key={status}
                              type="button"
                              className={`cmg-filter-chip${on ? ' on' : ''}`}
                              onClick={() =>
                                updateTargeting((state) => ({
                                  ...state,
                                  statuses: on
                                    ? state.statuses.filter((entry) => entry !== status)
                                    : [...state.statuses, status],
                                }))
                              }
                            >
                              {STATUS_LABELS[status] ?? status}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {audienceFilterMeta.priorities.length > 0 && (
                    <div className="cmg-filter-row">
                      <span className="cmg-field-label">Priority</span>
                      <div className="cmg-filter-chips">
                        {audienceFilterMeta.priorities.map(({ value, label }) => {
                          const on = filterState.priorities.includes(value);
                          return (
                            <button
                              key={value}
                              type="button"
                              className={`cmg-filter-chip${on ? ' on' : ''}`}
                              onClick={() =>
                                updateTargeting((state) => ({
                                  ...state,
                                  priorities: on
                                    ? state.priorities.filter((entry) => entry !== value)
                                    : [...state.priorities, value],
                                }))
                              }
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {audienceFilterMeta.vehicleTypes.length > 0 && (
                    <div className="cmg-filter-row">
                      <span className="cmg-field-label">Vehicle type</span>
                      <div className="cmg-filter-chips">
                        {audienceFilterMeta.vehicleTypes.map(({ value, label }) => {
                          const on = filterState.vehicleTypes.includes(value);
                          return (
                            <button
                              key={value}
                              type="button"
                              className={`cmg-filter-chip${on ? ' on' : ''}`}
                              onClick={() =>
                                updateTargeting((state) => ({
                                  ...state,
                                  vehicleTypes: on
                                    ? state.vehicleTypes.filter((entry) => entry !== value)
                                    : [...state.vehicleTypes, value],
                                }))
                              }
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {audienceFilterMeta.address.length > 0 && (
                    <div className="cmg-filter-row">
                      <span className="cmg-field-label">Address</span>
                      <div className="cmg-filter-address">
                        {ADDRESS_FIELDS.filter(({ field }) =>
                          audienceFilterMeta.address.includes(field),
                        ).map(({ field, label }) => (
                          <input
                            key={field}
                            className="cmg-input"
                            value={filterState.address[field]}
                            placeholder={label}
                            onChange={(e) =>
                              updateTargeting((state) => ({
                                ...state,
                                address: { ...state.address, [field]: e.target.value },
                              }))
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {audienceFilterMeta.bookings?.available && (
                    <div className="cmg-filter-row" data-filter="bookings">
                      <span className="cmg-field-label">Reservation history</span>
                      {/* One column: the presets, the fields they fill, then the hint.
                          As siblings of the label the three split the row three ways
                          and the fields were too narrow to read their own placeholders. */}
                      <div className="cmg-filter-stack">
                        <div className="cmg-filter-chips">
                          {BOOKING_PRESETS.map((preset) => {
                            const on =
                              JSON.stringify(filterState.bookings) === JSON.stringify(preset.state);
                            return (
                              <button
                                key={preset.key}
                                type="button"
                                title={preset.hint}
                                className={`cmg-filter-chip${on ? ' on' : ''}`}
                                onClick={() =>
                                  updateTargeting((state) => ({
                                    ...state,
                                    bookings: on ? { ...EMPTY_BOOKINGS } : { ...preset.state },
                                  }))
                                }
                              >
                                {preset.label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="cmg-filter-address">
                          {(
                            [
                              ['lastTripWithinDays', 'Rode in the last … days'],
                              ['noTripForDays', 'No trip for … days'],
                              ['minTrips', 'At least … trips'],
                              ['maxTrips', 'At most … trips'],
                              ...(audienceFilterMeta.bookings.spend
                                ? ([['minSpend', `Spent at least ${tenantCurrencySymbol()}…`]] as const)
                                : []),
                            ] as const
                          ).map(([field, label]) => (
                            <input
                              key={field}
                              className="cmg-input"
                              type="number"
                              min={0}
                              value={filterState.bookings[field]}
                              placeholder={label}
                              title={label}
                              onChange={(e) =>
                                updateTargeting((state) => ({
                                  ...state,
                                  bookings: { ...state.bookings, [field]: e.target.value },
                                }))
                              }
                            />
                          ))}
                          <select
                            className="cmg-input"
                            value={filterState.bookings.upcoming}
                            title="A trip already booked for the future"
                            onChange={(e) =>
                              updateTargeting((state) => ({
                                ...state,
                                bookings: {
                                  ...state.bookings,
                                  upcoming: e.target.value as BookingFilterState['upcoming'],
                                },
                              }))
                            }
                          >
                            <option value="">Upcoming trip: either</option>
                            <option value="only">Only if a trip is booked</option>
                            <option value="exclude">Only if nothing is booked</option>
                          </select>
                        </div>
                        <span className="cmg-hint">
                          Read from your dispatch jobs when the campaign sends, so it reaches whoever
                          matches on the day. Quotes, cancellations and no-shows are not trips.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Two answers, not three controls. This row used to carry a
                      mode pair AND a See-the-list toggle AND a second chip pair
                      inside the list whose first chip was also called "All
                      matching" — so "Only people I pick" and "All matching"
                      could both be lit at once, which reads as a contradiction
                      even when the campaign underneath is correct. The question
                      is only ever: everyone, or the ones I choose. */}
                  <div className="cmg-filter-row">
                    <span className="cmg-field-label">Recipients</span>
                    <div className="cmg-filter-chips">
                      <button
                        type="button"
                        className={`cmg-filter-chip${picking ? '' : ' on'}`}
                        title="Everyone the filters above match, resolved fresh when it sends"
                        onClick={() => {
                          setPickingOpen(false);
                          selectEveryone();
                        }}
                      >
                        All matching
                        {filteredCount !== null && !picking && ` · ${filteredCount.toLocaleString()}`}
                      </button>
                      <button
                        type="button"
                        className={`cmg-filter-chip${picking ? ' on' : ''}`}
                        title="Open the list and tick exactly who this goes to"
                        onClick={() => !picking && openPicker('pool')}
                      >
                        Choose from the list
                      </button>
                    </div>
                  </div>

                  {picking && (
                    <div className="cmg-members">
                      {/* Select all / Clear are the two ends of the same axis and
                          they are what make a list of thousands usable: ticking
                          1,200 of 1,250 by hand is not a thing anybody does, so
                          "everyone except these fifty" has to start from
                          everyone. Select all is NOT 1,250 stored addresses —
                          it is the all-matching query with nothing carved out,
                          which is also why it stays right when the audience
                          grows between now and the send. */}
                      <div className="cmg-filter-chips">
                        {/* A VIEW switch, and labelled as one. These used to be
                            chips called "All matching" and "On this list", which
                            is the same wording as the mode above them — two
                            controls that look identical and answer different
                            questions is what made this screen unreadable. */}
                        <span className="cmg-field-label">Showing</span>
                        <button
                          type="button"
                          className={`cmg-filter-chip${pickerScope === 'pool' ? ' on' : ''}`}
                          title="Everyone the filters match — the list picks are made from"
                          onClick={() => pickerScope !== 'pool' && openPicker('pool')}
                        >
                          Everyone
                        </button>
                        <button
                          type="button"
                          className={`cmg-filter-chip${pickerScope === 'selected' ? ' on' : ''}`}
                          title="Exactly who this campaign goes to"
                          onClick={() => pickerScope !== 'selected' && openPicker('selected')}
                        >
                          {/* "Selected" and not "Going out": opted-out people
                              are inside this number and are screened at send
                              time, so a label promising delivery would be
                              wrong by however many of them there are. What
                              actually leaves is the pace card's business. */}
                          Selected
                          {recipientCount !== null && ` · ${recipientCount.toLocaleString()}`}
                        </button>
                        <span style={{ flex: 1 }} />
                        <button
                          type="button"
                          className="cbtn"
                          style={{ height: 28 }}
                          disabled={allSelected}
                          title="Everyone matching these filters, including people added before it sends"
                          // Pins the list open as well as ticking everybody:
                          // `picking` falls back to whether any picks are
                          // STORED, and Select all is precisely the state that
                          // stores none — so on a draft reopened with
                          // exclusions this button would otherwise close the
                          // list it was pressed in.
                          onClick={() => {
                            setPickingOpen(true);
                            selectEveryone();
                          }}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="cbtn"
                          style={{ height: 28 }}
                          disabled={manualEmpty}
                          title="Untick everybody and start again"
                          onClick={selectNobody}
                        >
                          Clear
                        </button>
                      </div>
                      <input
                        className="cmg-input"
                        value={memberQuery}
                        placeholder={
                          isSms ? 'Search by name or number…' : 'Search by name or email…'
                        }
                        onChange={(e) => {
                          setMemberQuery(e.target.value);
                          setMembersPage(1);
                        }}
                      />
                      <div className="cmg-members-list">
                        {visibleMembers.map((member) => {
                          /**
                           * Somebody the send will drop. The row shows UNTICKED
                           * and refuses the click while the override is off,
                           * whatever the mode says — under Select all they are
                           * ticked in the stored state and screened out at send
                           * time, and a ticked box over a person who will not be
                           * contacted is the picker lying about the audience.
                           * With the override on they behave like any other row.
                           */
                          const blocked = !!member.optedOut && !contactOptedOut;
                          const address = memberAddress(member);
                          return (
                          <label
                            key={address}
                            className="cmg-member"
                            style={blocked ? { opacity: 0.55 } : undefined}
                            title={
                              blocked
                                ? 'This person has opted out and will be skipped. "Send to people who have opted out" is in the Throttle step.'
                                : undefined
                            }
                          >
                            <input
                              type="checkbox"
                              checked={!blocked && memberChecked(address)}
                              disabled={blocked}
                              onChange={() => toggleMember(address)}
                            />
                            <span className="cmg-member-name">
                              {member.name || address}
                            </span>
                            <span className="cmg-member-mail">{address}</span>
                            {member.optedOut && (
                              <span className="cmg-member-tag">Opted out</span>
                            )}
                            {member.status && (
                              <span className="cmg-member-tag">
                                {STATUS_LABELS[member.status] ?? member.status}
                              </span>
                            )}
                            {member.priority !== null && member.priority > 1 && (
                              <span className="cmg-member-tag vip">
                                {PRIORITY_LABELS[member.priority] ?? member.priority}
                              </span>
                            )}
                            {member.vehicleType && (
                              <span className="cmg-member-tag">{member.vehicleType}</span>
                            )}
                          </label>
                          );
                        })}
                        {!membersLoading && visibleMembers.length === 0 && (
                          <div className="cmg-hint">
                            {pickerScope === 'selected'
                              ? 'Nobody is picked yet — tick people under Everyone, or press Select all.'
                              : 'No one matches these filters.'}
                          </div>
                        )}
                        {membersLoading && <div className="cmg-hint">Loading…</div>}
                        {/* Last thing in the scroller: reaching it IS the ask
                            for the next page. Load more stays in the foot for
                            the times the observer cannot fire. */}
                        <div ref={moreMembers} aria-hidden />
                      </div>
                      <div className="cmg-members-foot">
                        <span className="cmg-hint">
                          {visibleTotal.toLocaleString()}{' '}
                          {pickerScope === 'selected' ? 'selected' : 'match'} ·{' '}
                          {/* What is actually going out, said in the shape that
                              is stored: a count when it is a list of names, and
                              the standing query when it is not — "1,250 picked"
                              over Select all would be a number nobody chose and
                              one that is wrong the moment a customer signs up. */}
                          {filterState.mode === 'manual'
                            ? `${filterState.selected.length.toLocaleString()} picked`
                            : filterState.excluded.length
                              ? `everyone except ${filterState.excluded.length.toLocaleString()}`
                              : 'all of them'}
                          {optedOutShown
                            ? ` · ${optedOutShown.toLocaleString()} opted out${contactOptedOut ? ', contacted anyway' : ', skipped'}`
                            : ''}
                        </span>
                        {visibleMembers.length < visibleTotal && (
                          <button
                            type="button"
                            className="cbtn"
                            style={{ height: 28 }}
                            disabled={membersLoading}
                            onClick={() => setMembersPage((page) => page + 1)}
                          >
                            Load more
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="cmg-divider" />
              <div className="cmg-upload">
                <Upload size={20} />
                <div style={{ flex: 1 }}>
                  <div className="cmg-upload-title">Or upload your own list</div>
                  <div className="cmg-upload-sub">
                    CSV or plain text — one recipient per line,{' '}
                    {isSms ? '"+15165550100, name"' : '"email, name"'}. Duplicates are dropped
                    here;{' '}
                    {isSms
                      ? 'anyone who has replied STOP is skipped at send time.'
                      : 'nothing else is, so take out anyone who has asked to stop.'}
                  </div>
                </div>
                <button type="button" className="cbtn" onClick={() => fileInput.current?.click()}>
                  Choose file
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".csv,.txt"
                  hidden
                  onChange={(e) => void pickFile(e.target.files)}
                />
              </div>
              {audience === 'custom' && (
                <>
                  <textarea
                    className="cmg-custom-list"
                    data-tour="custom-list"
                    value={customText}
                    onChange={(e) => {
                      setCustomEdited(true);
                      setCustomText(e.target.value);
                    }}
                    placeholder={'rhea@example.com, Rhea Kapoor\nops@eliterides.ae'}
                  />
                  <div className="cmg-hint">
                    {customRows.length.toLocaleString()} valid recipient
                    {customRows.length === 1 ? '' : 's'} parsed.
                  </div>
                </>
              )}
              {audience === 'drivers' && (
                <div className="cmg-warnbox">
                  <TriangleAlert size={15} />
                  <span>
                    Chauffeur and affiliate lists are operational contacts. Sending marketing to
                    them without consent risks spam complaints — keep those to briefings.
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="cmg-card" style={{ gap: 12 }}>
              <div className="cmg-work-head">
                <h2>What does it say?</h2>
                <p>The preview beside you shows it with your real company details as you type.</p>
              </div>

              <div className="cmg-row">
                <div className="cmg-field" data-tour="message-name">
                  <span className="cmg-field-label">Campaign name</span>
                  <input
                    className="cmg-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Spring corporate offer"
                  />
                </div>
                {/* A text carries no display name — the recipient sees the
                    number, and there is nowhere to put one. */}
                {!isSms && (
                  <div className="cmg-field">
                    <span className="cmg-field-label">From name</span>
                    <input
                      className="cmg-input"
                      value={fromName}
                      onChange={(e) => setFromName(e.target.value)}
                      placeholder="LimoSphere"
                    />
                  </div>
                )}
              </div>
              <div className="cmg-row">
                <div className="cmg-field">
                  <span className="cmg-field-label">{isSms ? 'From number' : 'From mailbox'}</span>
                  <select
                    className="cmg-select"
                    value={effectiveInboxId ?? ''}
                    onChange={(e) => setInboxId(Number(e.target.value))}
                  >
                    {senderInboxes.length === 0 && (
                      <option value="">
                        {isSms ? 'No SMS number connected' : 'No email inbox connected'}
                      </option>
                    )}
                    {senderInboxes.map((inbox) => (
                      <option key={inbox.id} value={inbox.id}>
                        {identityLine(inbox)} · {inbox.name}
                        {needsReauth(inbox) ? ' · needs reconnecting' : ''}
                      </option>
                    ))}
                  </select>
                  {/* Said at the picker, not only on the review step: the
                      mailbox is chosen here, and a campaign written against a
                      dead one is an hour of copy that cannot go out. A stale
                      mailbox is still LISTED — it is the right sender once it is
                      reconnected, and dropping it would read as the channel
                      having been deleted. */}
                  {selectedInbox && needsReauth(selectedInbox) && (
                    <span className="cmg-field-warn">
                      {identityLine(selectedInbox)} lost its authorization. Reconnect it before
                      sending — every recipient would bounce off the same credentials.
                    </span>
                  )}
                </div>
                {!isSms && (
                  <div className="cmg-field">
                    <span className="cmg-field-label">Subject</span>
                    <input
                      ref={subjectInput}
                      className="cmg-input"
                      value={subject}
                      onFocus={() => setInsertTarget('subject')}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="{{name}}, priority airport transfers for your team"
                    />
                  </div>
                )}
              </div>
              {isSms ? (
                <div className="cmg-field" data-tour="message-body">
                  <span className="cmg-field-label">Text message</span>
                  <textarea
                    className="cmg-input"
                    style={{ minHeight: 140, resize: 'vertical', fontFamily: 'inherit' }}
                    value={bodySms}
                    onFocus={() => setInsertTarget('body')}
                    onChange={(e) => setBodySms(e.target.value)}
                    placeholder="Hi {{name}} — airport transfers are 15% off this month. Reply STOP to opt out."
                  />
                  {/**
                   * The cost, live. Segments and not characters, because that is
                   * what is billed, what the carrier meters and what the
                   * number's throughput is spent in — and because a single
                   * pasted curly apostrophe doubles it without changing the
                   * character count at all, which is the one thing an operator
                   * cannot see for themselves.
                   */}
                  <span
                    className="cmg-field-hint"
                    style={{
                      color:
                        smsCost.segments > CAMPAIGN_MAX_SEGMENTS
                          ? 'var(--c-red)'
                          : smsCost.encoding === 'UCS-2'
                            ? 'var(--c-orange)'
                            : undefined,
                    }}
                  >
                    {smsCostLabel(smsCost)}
                    {smsCost.segments > CAMPAIGN_MAX_SEGMENTS
                      ? ` · over the ${CAMPAIGN_MAX_SEGMENTS}-segment limit`
                      : ''}
                  </span>
                  <span className="cmg-field-hint">
                    Each recipient's copy is counted on its own — {'{{name}}'} makes some messages
                    longer than others, so a few may cost an extra segment.
                  </span>
                </div>
              ) : (
                <div className="cmg-field" data-tour="message-body">
                  <span className="cmg-field-label">Body</span>
                  <div onFocus={() => setInsertTarget('body')}>
                    <RichText
                      ref={bodyEditor}
                      value={body}
                      onChange={setBody}
                      placeholder="Hi {{name}} — flight-tracked pickups, meet & greet, and one consolidated monthly invoice."
                    />
                  </div>
                </div>
              )}

              {/* The data points and saved blocks, folded behind one line: they
                  are reached for a few times per campaign, and open they were
                  most of the screen under the editor. */}
              {(mergeGroups.length > 0 || (templateBlocks.length > 0 && !isSms)) && (
                <div className="cmg-insert">
                  <button
                    type="button"
                    className="cmg-insert-toggle"
                    aria-expanded={insertOpen}
                    onClick={() => setInsertOpen((open) => !open)}
                  >
                    <Braces size={14} />
                    <span>
                      Insert {isSms ? 'a data point' : 'a data point or a saved block'}
                    </span>
                    <span className="cmg-tags-target">
                      {insertOpen
                        ? `into ${insertTarget === 'subject' ? 'the subject' : 'the body'}`
                        : 'their name, your company’s details'}
                    </span>
                    <ChevronDown size={14} className={insertOpen ? 'cmg-insert-chevron open' : 'cmg-insert-chevron'} />
                  </button>
                  {insertOpen && (
                    <>
                  {mergeGroups.length > 0 && (
                    <div className="cmg-tags">
                      <div className="cmg-tags-head">
                        <Braces size={14} />
                        <span>Data points</span>
                        <span className="cmg-tags-target">
                          inserts into {insertTarget === 'subject' ? 'the subject' : 'the body'}
                        </span>
                      </div>
                      {mergeGroups.map((group) => (
                        <div key={group.key} className="cmg-tags-group">
                          <span className="cmg-tags-group-label">{group.label}</span>
                          <div className="cmg-tags-row">
                            {group.fields.map((field) => (
                              <button
                                key={field.key}
                                type="button"
                                className="cmg-tag"
                                title={field.hint ?? `${field.label}${field.value ? ` — ${field.value}` : ''}`}
                                // Keep the caret where it is: a focus change would
                                // drop the tag at the start of the field instead.
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => insertTag(field.tag)}
                              >
                                <code>{field.tag}</code>
                                <span className="cmg-tag-label">{field.value ?? field.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      <div className="cmg-hint">
                        Click one to drop it in. Recipient values come from the audience
                        {mergeGroups.some((group) => group.key === 'company')
                          ? '; company values come from your dispatch company profile and are the same for everyone on the list'
                          : ''}
                        . A tag that is not listed here is mailed out exactly as typed.
                      </div>
                    </div>
                  )}

                  {/* The dashboard's reusable html blocks — a letterhead, a
                      signature. Nothing to insert into a text message. */}
                  {templateBlocks.length > 0 && !isSms && (
                    <div className="cmg-tags">
                      <div className="cmg-tags-head">
                        <LayoutTemplate size={14} />
                        <span>Blocks</span>
                        {/* No target switch like the data points above: a block is a
                            piece of layout, and a subject line is plain text. */}
                        <span className="cmg-tags-target">inserts into the body</span>
                      </div>
                      <div className="cmg-tags-row">
                        {templateBlocks.map((block) => (
                          <button
                            key={block.id}
                            type="button"
                            className="cmg-tag"
                            title={block.hint || 'Saved in Templates → Components'}
                            // As above: the caret is where this lands, so the press
                            // must not move focus.
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => bodyEditor.current?.insertHtml(block.html)}
                          >
                            <span className="cmg-tag-label">{block.name}</span>
                          </button>
                        ))}
                      </div>
                      <div className="cmg-hint">
                        Your saved email blocks, from Templates → Components in the dashboard. The
                        company details inside one are already filled in — the same for everyone on
                        the list — so a block needs no data points of its own.
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </div>
              )}

              {isSms && (
                <div className="cmg-track">
                  <div className="cmg-card-title" style={{ marginTop: 6 }}>
                    Link clicks
                  </div>
                  <label className="cmg-member" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={trackClicks} onChange={() => setTrackClicks((on) => !on)} />
                    <span className="cmg-member-name">Shorten links and count who taps them</span>
                  </label>
                  <div className="cmg-hint" style={{ marginTop: 6 }}>
                    Each link becomes a short one
                    {shortBase ? (
                      <>
                        {' '}
                        like <code>{shortBase}/x7Kp9Qa</code>
                      </>
                    ) : null}
                    , unique to each recipient, so Results can show who tapped it. The segment count
                    above already uses the shortened text.
                    {shortLinkState.data && !shortLinkState.data.domain?.verified
                      ? ' Set up your own link domain on the Link domain tab — carriers trust links on your own domain far more than shared ones.'
                      : ''}
                  </div>
                </div>
              )}

              {/* Email only: on a text the three parameters are about fifty
                  characters — a second segment for most recipients — so a text
                  campaign is never tagged and the server stores no name. */}
              {!isSms && (
                <div className="cmg-track" data-tour="tracking">
                  <div className="cmg-card-title" style={{ marginTop: 6 }}>
                    Website tracking
                  </div>
                  <label className="cmg-member" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={trackLinks}
                      onChange={() => setTrackLinks((on) => !on)}
                    />
                    <span className="cmg-member-name">
                      Track the visits and reservations this email brings to your website
                    </span>
                  </label>
                  {trackLinks && (
                    <>
                      <div className="cmg-row" style={{ marginTop: 10 }}>
                        <div className="cmg-field">
                          <span className="cmg-field-label">Tracking name</span>
                          <input
                            className="cmg-input"
                            value={trackingTyped ?? trackingName}
                            maxLength={80}
                            placeholder="spring-corporate-offer"
                            onChange={(e) => setTrackingTyped(e.target.value)}
                            // Tidied on the way out of the box rather than under
                            // the caret, so typing a space is not eaten; an empty
                            // box goes back to following the campaign name.
                            onBlur={() =>
                              setTrackingTyped((typed) =>
                                typed === null ? null : tidyTrackingName(typed) || null,
                              )
                            }
                          />
                          <span className="cmg-hint">
                            {trackingName ? (
                              <>
                                Listed as <code>{trackingName}</code> on the Analytics Campaigns card.
                                Keep it the same when you run this offer again, and the visits add up
                                in one row.
                              </>
                            ) : (
                              'Needs at least one letter or number — until then links go out as written.'
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="cmg-hint" style={{ marginTop: 6 }}>
                        {linkSites === null ? (
                          'Checking which links point at your website…'
                        ) : linkSites === 'failed' ? (
                          'Could not load your website’s address, so this preview cannot say which links will be tagged. The send checks again for itself.'
                        ) : ownHosts.length === 0 ? (
                          <>
                            We do not know your website’s address, so no link will be tagged. Add it
                            as the Website on your company profile in the dispatch dashboard, or under
                            Allowed sites on an Analytics install.
                          </>
                        ) : (
                          <>
                            {linkSummary.tagged > 0 ? (
                              <>
                                <b>
                                  {linkSummary.tagged} link{linkSummary.tagged === 1 ? '' : 's'}
                                </b>{' '}
                                to {linkSummary.taggedHosts.join(', ')}{' '}
                                {linkSummary.tagged === 1 ? 'carries' : 'carry'} this name when it
                                sends.
                              </>
                            ) : (
                              <>
                                No link in this email points at {ownHosts.join(' or ')} yet — only
                                links to your own website are tagged.
                              </>
                            )}
                            {linkSummary.handTagged > 0
                              ? ` ${linkSummary.handTagged} already ${linkSummary.handTagged === 1 ? 'has' : 'have'} its own tracking and ${linkSummary.handTagged === 1 ? 'is' : 'are'} left alone.`
                              : ''}
                            {linkSummary.other > 0
                              ? ` ${linkSummary.other} to ${linkSummary.otherHosts.slice(0, 3).join(', ')}${linkSummary.otherHosts.length > 3 ? '…' : ''} ${linkSummary.other === 1 ? 'is' : 'are'} left as written.`
                              : ''}
                            {linkSites.webInstalls === 0
                              ? ' Nothing is counting visits on your website yet — set up Analytics to see them.'
                              : ''}
                          </>
                        )}
                      </div>
                    </>
                  )}
                  <label className="cmg-member" style={{ cursor: 'pointer', marginTop: 10 }}>
                    <input
                      type="checkbox"
                      checked={trackClicks}
                      onChange={() => setTrackClicks((on) => !on)}
                    />
                    <span className="cmg-member-name">Count who clicks each link</span>
                  </label>
                  <div className="cmg-hint" style={{ marginTop: 6 }}>
                    Links pass through a short redirect so Results can show clicks per link and let you
                    follow up with the people who clicked. Corporate mail scanners that open every link are
                    detected and counted apart. Works for every link, not only your website’s.
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="cmg-card">
              <div className="cmg-work-head">
                <h2>How it’s sent</h2>
                <p>Sensible defaults are already set — change only what matters for this one.</p>
              </div>

              {/* Each setting is a sentence with its value in it — "Send up to
                  30 emails a minute" — rather than a label, a box and a
                  paragraph explaining the box. */}
              <section className="cmg-section">
                <div className="cmg-card-title">Pace</div>
                <div className="cmg-sentence" data-tour="throttle">
                  <span>Send up to</span>
                  <input
                    className="cmg-input cmg-inline-num"
                    type="number"
                    min={1}
                    max={120}
                    aria-label={isSms ? 'Texts a minute' : 'Emails a minute'}
                    value={throttle}
                    onChange={(e) => setThrottle(Number(e.target.value) || 30)}
                  />
                  <span>{isSms ? 'texts' : 'emails'} a minute</span>
                  <span className="cmg-sentence-note">
                    {/* A text is metered in SEGMENTS, not messages, so a
                        per-hour figure counted in messages is the wrong unit by
                        whatever this copy costs — quote what the number buys. */}
                    {isSms
                      ? `· about ${(throttle * 60 * smsCost.segments).toLocaleString()} segments an hour${
                          smsCost.segments > 1 ? ` (${throttle * 60} texts at ${smsCost.segments} each)` : ''
                        }`
                      : `· about ${(throttle * 60).toLocaleString()} an hour`}
                  </span>
                </div>
                <div className="cmg-hint">
                  {isSms
                    ? 'A ceiling, not the rate: a campaign only gets the share of this number the agents are not using, so a busy line sends slower and a quiet one reaches it. The real cap is at your carrier and depends on how the number is registered.'
                    : spreadDays
                      ? `The real ceiling is the mailbox’s own daily allowance: this list takes about ${spreadDays} days to clear at it, and a higher pace sends each day’s share sooner rather than more of it. Change the allowance in the channel’s settings.`
                      : 'Sending goes through the mailbox itself, so its provider’s daily caps apply (Gmail about 500 a day on a personal account, about 2,000 on Workspace).'}
                </div>
              </section>

              <section className="cmg-section" data-card="delivery-controls">
                <div className="cmg-card-title">When it may send</div>
                <div className="cmg-sentence">
                  <label className="cmg-sentence-check">
                    <input type="checkbox" checked={quietOn} onChange={() => setQuietChoice(!quietOn)} />
                    <span>Only send between</span>
                  </label>
                  <input
                    className="cmg-input cmg-inline-time"
                    type="time"
                    aria-label="From"
                    disabled={!quietOn}
                    value={minutesToClock(windowStart)}
                    onChange={(e) => setWindowStart(clockToMinutes(e.target.value) ?? windowStart)}
                  />
                  <span>and</span>
                  <input
                    className="cmg-input cmg-inline-time"
                    type="time"
                    aria-label="Until"
                    disabled={!quietOn}
                    value={minutesToClock(windowEnd)}
                    onChange={(e) => setWindowEnd(clockToMinutes(e.target.value) ?? windowEnd)}
                  />
                  <span>company time{tenantZoneLabel ? ` (${tenantZoneLabel})` : ''}</span>
                </div>
                <div className="cmg-hint">
                  {quietOn
                    ? windowStart === windowEnd
                      ? 'Pick two different times.'
                      : `A send still going at ${minutesToClock(windowEnd)} pauses and picks up again at ${minutesToClock(windowStart)}${windowStart > windowEnd ? ' — this window runs overnight' : ''}.`
                    : isSms
                      ? 'Texts can go out at any hour. Carriers and US law (TCPA) expect marketing texts only between 8am and 9pm in the recipient’s time.'
                      : 'Sends at any hour once it starts.'}
                </div>
                <div className="cmg-sentence">
                  <span>Skip anyone another {isSms ? 'text' : 'email'} campaign reached in the last</span>
                  <select
                    className="cmg-input cmg-inline-select"
                    aria-label="Skip people who recently got another campaign"
                    value={frequencyCap ?? ''}
                    onChange={(e) => setFrequencyCap(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">— don’t skip anyone</option>
                    {FREQUENCY_CAPS.map((cap) => (
                      <option key={cap.hours} value={cap.hours}>
                        {cap.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cmg-hint">
                  The skipped are listed on Results as “recently contacted”.
                </div>
              </section>

              <section className="cmg-section">
                <div className="cmg-card-title">Opting out</div>
                <div className="cmg-sentence">
                  <label className="cmg-sentence-check">
                    <input type="checkbox" checked={unsubFooter} onChange={() => setUnsubFooter((on) => !on)} />
                    <span>
                      {isSms ? 'End the text with “Reply STOP to opt out.”' : 'Add an unsubscribe link to the footer'}
                    </span>
                  </label>
                  {unsubFooter && !isSms && (
                    <button type="button" className="cmg-link-btn" onClick={() => setUnsubWordingOpen((open) => !open)}>
                      {unsubWordingOpen ? 'Hide wording' : 'Edit wording'}
                    </button>
                  )}
                </div>
                <div className="cmg-hint">
                  {isSms
                    ? 'STOP always works whether this is on or not. Saying so is what makes someone reply STOP instead of reporting the number — and it costs real characters, already in the segment count.'
                    : 'Mail clients show their own Unsubscribe button either way; turning this off removes the visible footer, for an operational notice rather than marketing.'}
                </div>
                {unsubFooter && !isSms && unsubWordingOpen && (
                  <>
                    <div className="cmg-row">
                      <div className="cmg-field">
                        <span className="cmg-field-label">Footer wording</span>
                        <input
                          className="cmg-input"
                          value={unsubText}
                          maxLength={300}
                          placeholder={companyFooter?.text ?? defaultFooterText(tenantMerge.company_name)}
                          onChange={(e) => setUnsubText(e.target.value)}
                        />
                      </div>
                      <div className="cmg-field">
                        <span className="cmg-field-label">Link wording</span>
                        <input
                          className="cmg-input"
                          value={unsubLinkText}
                          maxLength={60}
                          placeholder={companyFooter?.linkText ?? DEFAULT_LINK_TEXT}
                          onChange={(e) => setUnsubLinkText(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="cmg-hint">
                      Empty uses the greyed-out wording — {companyFooter?.text || companyFooter?.linkText
                        ? 'your company’s, from Settings → Opting out'
                        : 'the built-in one, until your company sets its own in Settings → Opting out'}
                      — so what you see is what goes out. Merge tags work here — a tag nothing fills is
                      refused at send, the same as one in the body. The link itself always works, whatever
                      you call it.
                    </div>
                  </>
                )}
                {/* The list itself, which is a different question from the footer
                    above: that one is whether to SHOW a way out, this one is
                    whether to honour one already taken. Unticked is the answer on
                    every campaign nobody touches. */}
                <label
                  className="cmg-sentence-check"
                  style={{ cursor: mayApprove ? 'pointer' : 'not-allowed', marginTop: 6, fontSize: 13.5 }}
                  title={
                    mayApprove
                      ? undefined
                      : 'Contacting people who have opted out needs Campaigns at Send'
                  }
                >
                  <input
                    type="checkbox"
                    checked={contactOptedOut}
                    disabled={!mayApprove}
                    onChange={() => setContactOptedOut((on) => !on)}
                  />
                  <span>
                    Send to people who have opted out
                    {optedOutShown ? ` · ${optedOutShown.toLocaleString()} on this list` : ''}
                  </span>
                </label>
                {contactOptedOut ? (
                  <div className="cmg-warnbox" style={{ marginTop: 8 }}>
                    <TriangleAlert size={15} />
                    <span>
                      This campaign will contact people who asked not to be. Keep it to operational
                      messages — a shift change, a depot closure — which an opt-out does not cover;
                      marketing to this list is what draws complaints and, on{' '}
                      {isSms ? 'text' : 'email'}, regulatory ones.{' '}
                      {isSms ? (
                        <>
                          It also cannot reach everybody: a STOP reply is enforced at the carrier,
                          which refuses the message whatever we send, so those recipients still show
                          as failed.
                        </>
                      ) : (
                        <>
                          It applies to this campaign only — the list itself is not changed, and the
                          next campaign starts from honouring it again.
                        </>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="cmg-hint" style={{ marginTop: 6 }}>
                    Anyone who has already opted out is skipped
                    {optedOutShown
                      ? ` — ${optedOutShown.toLocaleString()} of this audience`
                      : ' automatically, on every campaign'}
                    .
                  </div>
                )}
              </section>
            </div>
          )}

          {step === 4 && (
            <div className="cmg-card">
              <div className="cmg-work-head">
                <h2>Review &amp; send</h2>
                <p>Everything that decides how this send goes, checked — test it on yourself, then send it.</p>
              </div>

              {/* The sender and the list measured, the words read by the
                  software and by AI, the sending settings — one verdict.
                  Nothing in it blocks the send; it says what to look at and
                  offers the fix beside it. */}
              <CampaignPreflightCard
                request={preflightRequest}
                campaignId={savedId}
                words={{
                  channel,
                  subject,
                  bodyHtml: body.html,
                  bodyText: body.text,
                  bodySms,
                  audienceType: audience,
                  fromName,
                  empty: isSms ? !bodySms.trim() : !subject.trim() && !body.text.trim(),
                }}
                localRows={preflightRows}
                heads={preflightHeads}
                onAction={onPreflightAction}
                onStep={setStep}
                canApplySuggestion={(evidence) => placeSuggestion(evidence, '', false)}
              />

              <div data-card="test-send">
                <div className="cmg-card-title" style={{ marginTop: 18 }}>
                  Send a test
                </div>
                <div className="cmg-row" style={{ alignItems: 'flex-end' }}>
                  <div className="cmg-field">
                    <span className="cmg-field-label">
                      {isSms ? 'Phone number (blank = yours)' : 'Email address (blank = yours)'}
                    </span>
                    <input
                      className="cmg-input"
                      value={testTo}
                      placeholder={isSms ? 'Your dispatch phone' : 'Your dispatch email'}
                      onChange={(e) => setTestTo(e.target.value)}
                    />
                  </div>
                  <div className="cmg-field" style={{ flex: '0 0 auto' }}>
                    <button
                      type="button"
                      className="cbtn"
                      disabled={!readyToSave || testState.busy}
                      onClick={() => void sendTest()}
                    >
                      {testState.busy ? 'Sending…' : 'Send test'}
                    </button>
                  </div>
                </div>
                <div
                  className="cmg-hint"
                  style={{ marginTop: 6, color: testState.error ? 'var(--danger, #b42318)' : undefined }}
                >
                  {testState.note ??
                    (isSms
                      ? 'One text, with your merge values filled in. It saves the campaign first and does not count as a send.'
                      : 'One email marked [Test], with your merge values filled in. It saves the campaign first, spends no credits and does not count as a send.')}
                </div>
              </div>


              {/**
               * A text campaign has no delivery CHOICE — one transport, the
               * tenant's own number — so what goes here instead is the one
               * thing an operator cannot work out for themselves: how long it
               * will take, and why that will change while it runs. Without it a
               * campaign pacing itself around the agents reads as a stall.
               */}
              {isSmsOptions(deliveryOptions) && (
                <div className="cmg-delivery">
                  <div className="cmg-delivery-title">Pace</div>
                  <div className="cmg-delivery-note">{smsPaceSummary(deliveryOptions)}</div>
                  <div className="cmg-delivery-note">{smsReserveNote(deliveryOptions)}</div>
                  {deliveryOptions.dailyCapUnknown && (
                    <div className="cmg-delivery-note">
                      No daily limit is set for this number here. Your carrier may still have one —
                      a US 10DLC sender's allowance depends on how its brand is registered, and
                      nothing in the CRM can read it.
                    </div>
                  )}
                  {deliveryOptions.dailyRemaining !== null && (
                    <div className="cmg-delivery-note">
                      {deliveryOptions.dailyRemaining.toLocaleString()} of today's{' '}
                      {deliveryOptions.dailyLimit?.toLocaleString()} still available.
                    </div>
                  )}
                </div>
              )}

              {delivery && (
                <div className="cmg-delivery">
                  <div className="cmg-delivery-title">Delivery</div>
                  <div className="cmg-delivery-note">
                    This list is larger than one day of sending for this mailbox.
                    Sending it all at once would get the mailbox blocked, so pick
                    how to deliver it.
                  </div>
                  <button
                    type="button"
                    className={`cmg-delivery-opt${sendMode === 'spread' ? ' on' : ''}`}
                    onClick={() => setSendMode('spread')}
                  >
                    <span className="cmg-delivery-dot" />
                    <span>
                      <span className="cmg-delivery-opt-title">Send over several days</span>
                      <span className="cmg-delivery-opt-sub">{delivery.spreadDetail}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`cmg-delivery-opt${sendMode === 'ses' ? ' on' : ''}${
                      delivery.sesEnabled ? '' : ' off'
                    }`}
                    disabled={!delivery.sesEnabled}
                    onClick={() => setSendMode('ses')}
                  >
                    <span className="cmg-delivery-dot" />
                    <span>
                      <span className="cmg-delivery-opt-title">Send now using credits</span>
                      <span className="cmg-delivery-opt-sub">{delivery.sesDetail}</span>
                    </span>
                  </button>
                </div>
              )}
              {/* When, before How many — an operator who has decided on Friday
                  should not have to read the recipient count first to find the
                  button that books it. The two options are radios rather than a
                  toggle beside the button because "Send now" is a thing you want
                  to have chosen, not a thing you want to have left unchosen. */}
              {/* `cmg-when` carries no styles — it is the stable handle the
                  handbook's capture script clips, since `.cmg-delivery` is
                  also the credits choice above and only renders sometimes. */}
              <div className="cmg-delivery cmg-when">
                <div className="cmg-delivery-title">When</div>
                <button
                  type="button"
                  className={`cmg-delivery-opt${whenMode === 'now' ? ' on' : ''}`}
                  onClick={() => setWhenMode('now')}
                >
                  <span className="cmg-delivery-dot" />
                  <span>
                    <span className="cmg-delivery-opt-title">
                      {mayApprove ? 'Send now' : 'Send for review now'}
                    </span>
                    <span className="cmg-delivery-opt-sub">
                      {mayApprove
                        ? 'Starts going out within seconds, at the pace set in Throttle.'
                        : 'Goes to whoever reviews campaigns; they decide when it sends.'}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`cmg-delivery-opt${whenMode === 'later' ? ' on' : ''}${
                    mayApprove ? '' : ' off'
                  }`}
                  disabled={!mayApprove}
                  title={mayApprove ? undefined : 'Scheduling is part of sending — a reviewer picks the time'}
                  onClick={() => setWhenMode('later')}
                >
                  <span className="cmg-delivery-dot" />
                  <span>
                    <span className="cmg-delivery-opt-title">At a set time</span>
                    <span className="cmg-delivery-opt-sub">
                      Sits here until then. You can still edit it, move it, or call it off.
                    </span>
                  </span>
                </button>
                {whenMode === 'later' && (
                  <div className="cmg-field" style={{ marginTop: 4 }}>
                    <span className="cmg-field-label">
                      Date and time
                      {tenantZoneLabel ? ` · ${tenantZoneLabel}` : ''}
                    </span>
                    <input
                      className="cmg-input"
                      type="datetime-local"
                      value={scheduleWall}
                      onChange={(e) => setScheduleWall(e.target.value)}
                    />
                    {/* The instant this actually books, written back in the
                        tenant's own shape. The picker renders in the browser's
                        locale and reads as zone-less, so this line is the only
                        thing on screen that says which clock it landed on. */}
                    {timeBlocker ? (
                      <div className="cmg-hint">{timeBlocker}.</div>
                    ) : (
                      <div className="cmg-hint">
                        Goes out {fmtDateTime(instantFromWall(scheduleWall, zone)?.toISOString() ?? null)}
                        {tenantZoneLabel ? ` (${tenantZoneLabel})` : ''}.
                      </div>
                    )}
                    <div className="cmg-hint">
                      Who receives it is worked out when it sends, not now — anyone who joins
                      the list before then is included.
                      {effectiveSendMode === 'ses'
                        ? ' Credits are charged then too, so top up before the date or it will not go out.'
                        : ''}
                    </div>
                  </div>
                )}
              </div>
              <div className="cmg-recipients-box">
                <div>
                  <div className="cmg-recipients-cap">Recipients</div>
                  <div className="cmg-recipients-num">
                    {recipientCount === null ? '…' : recipientCount.toLocaleString()}
                  </div>
                </div>
                <div className="cmg-vline" />
                <div>
                  <div className="cmg-recipients-cap">Suppressed at send</div>
                  {/* Both channels screen the do-not-contact list, and this
                      line has to say so: promising "nobody" and then dropping
                      rows makes the operator read the gap in Results as a
                      fault. Bounces are still NOT screened — that stays the
                      operator's job and the Everyone warning says it.

                      The COUNT is deliberately not quoted: it is resolved at
                      send time against a list that can change between now and
                      then, and a number here that Results contradicts is worse
                      than no number. */}
                  <div className="cmg-recipients-cap" style={{ marginTop: 6 }}>
                    {isSms
                      ? 'anyone who has replied STOP — Results will say how many'
                      : 'anyone who has unsubscribed — Results will say how many'}
                  </div>
                </div>
                {whenMode === 'later' ? (
                  <button
                    type="button"
                    data-tour="launch"
                    className="cmg-launch"
                    disabled={!readyToSchedule || busy !== null}
                    title={scheduleBlocked ?? undefined}
                    onClick={() => void schedule()}
                  >
                    <CalendarClock size={16} />
                    {busy === 'schedule'
                      ? 'Scheduling…'
                      : draft?.status === 'scheduled'
                        ? 'Move it'
                        : 'Schedule it'}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-tour="launch"
                    className="cmg-launch"
                    disabled={!readyToSend || busy !== null}
                    title={sendBlocker ?? undefined}
                    onClick={() => void launch()}
                  >
                    <Rocket size={16} />
                    {busy === 'send'
                      ? mayApprove
                        ? 'Starting…'
                        : 'Sending for review…'
                      : mayApprove
                        ? 'Send campaign'
                        : 'Send for review'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <aside className="cmg-context" aria-label="About this step">
          {step === 1 && (
            <div className="cmg-context-card" data-card="who-matches">
              <div className="cmg-context-title">Who matches</div>
              <div className="cmg-context-big">
                {recipientCount === null ? '—' : recipientCount.toLocaleString()}
                {audience !== 'custom' &&
                recipientCount !== null &&
                audienceCounts[audience] !== undefined &&
                audienceCounts[audience] !== recipientCount ? (
                  <span className="cmg-context-of">
                    {' '}
                    of {(audienceCounts[audience] ?? 0).toLocaleString()}
                  </span>
                ) : null}
              </div>
              <div className="cmg-context-sub">
                {audience === 'custom'
                  ? 'on the list you gave'
                  : `${audienceLabel(audience).toLowerCase()}${targetingLine ? ` · ${targetingLine}` : ''}`}
              </div>
              <div className="cmg-context-rows">
                <div className="cmg-context-row">
                  <span>Skipped at send · opted out</span>
                  <b>{contactOptedOut ? 'not skipped' : optedOutShown.toLocaleString()}</b>
                </div>
                {frequencyCap ? (
                  <div className="cmg-context-row">
                    <span>
                      Skipped · another campaign in the last{' '}
                      {FREQUENCY_CAPS.find((cap) => cap.hours === frequencyCap)?.label ?? `${frequencyCap} hours`}
                    </span>
                    <b>at send</b>
                  </div>
                ) : null}
              </div>
              <div className="cmg-hint">
                {audience === 'custom'
                  ? 'Exactly the people you gave, less anyone who has opted out.'
                  : 'Read from dispatch at the moment it sends, so the list is always current — the count here is what it would be now.'}
              </div>
            </div>
          )}

          {step === 2 && (
            <>
            <div className="cmg-ai" data-tour="ai-assist">
              <div className="cmg-ai-head">
                <WandSparkles size={14} />
                <span>AI assist</span>
              </div>
              <div className="cmg-ai-tagline">
                {isSms ? 'Writes to the segment budget' : 'Knows your company details'}
              </div>
              {!aiOffered ? (
                <div className="cmg-hint">
                  AI assist is switched off for your role. An administrator can turn it on under
                  Settings → Users &amp; access.
                </div>
              ) : aiReady === false ? (
                /* No key resolves, so every press would fail with the same error.
                   Said here, beside the buttons it disables, with the way to the
                   one screen that fixes it. */
                <div className="cmg-ai-setup">
                  <p>
                    AI assist needs an AI key first. Add an OpenAI or Anthropic key once in{' '}
                    <b>AI settings</b>, and Generate and Improve work here and everywhere else the CRM
                    drafts for you.
                  </p>
                  {mayManageAi ? (
                    <>
                      <div className="cmg-ai-row">
                        <button type="button" className="cbtn cbtn-primary" onClick={openAiSettings}>
                          <KeyRound size={13} />
                          Set up AI
                          <ArrowUpRight size={13} />
                        </button>
                        <button type="button" className="cbtn" onClick={() => setAiProbe((n) => n + 1)}>
                          Check again
                        </button>
                      </div>
                      <div className="cmg-hint">
                        It opens in a new tab, so this campaign stays as it is. Come back and press
                        Check again.
                      </div>
                    </>
                  ) : (
                    <div className="cmg-hint">
                      Adding a key needs AI at Manage — ask whoever looks after your CRM settings.
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <input
                    className="cmg-input"
                    value={aiPrompt}
                    disabled={aiBusy !== null}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={
                      isSms
                        ? 'Describe the text to write, or how to improve it…'
                        : 'Describe the email to write, or how to improve the draft…'
                    }
                  />
                  <div className="cmg-ai-row">
                    <button
                      type="button"
                      className="cbtn"
                      disabled={aiBusy !== null || !aiPrompt.trim()}
                      title={
                        isSms
                          ? 'Write a fresh message from the brief'
                          : 'Write a fresh subject + body from the brief'
                      }
                      onClick={() => void runAi('generate')}
                    >
                      {aiBusy === 'generate' ? 'Writing…' : 'Generate'}
                    </button>
                    <button
                      type="button"
                      className="cbtn"
                      disabled={
                        aiBusy !== null || (isSms ? !bodySms.trim() : !subject.trim() && !body.text.trim())
                      }
                      title={
                        isSms
                          ? 'Shorten and sharpen the current message (the brief steers it when filled)'
                          : 'Polish the current subject + body (the brief steers it when filled)'
                      }
                      onClick={() => void runAi('improve')}
                    >
                      {aiBusy === 'improve' ? 'Improving…' : 'Improve'}
                    </button>
                  </div>
                  <div className="cmg-hint">
                    {isSms ? (
                      <>
                        Generate writes from your brief; Improve cuts what you have without losing it.
                        It writes to the {CAMPAIGN_MAX_SEGMENTS}-segment limit, sticks to plain characters so
                        the message does not go out as Unicode at double the cost, and leaves the opt-out
                        line to us. Review before sending.
                      </>
                    ) : (
                      <>
                        Generate writes from your brief; Improve polishes what you have. It writes with
                        your company name, city, phone and links already in hand — as data points, not
                        fixed text — and can place your logo. Review before sending.
                      </>
                    )}
                  </div>
                  {aiError && (
                    <div className="comms-error-note">
                      {aiError}
                      {/* A key that resolves can still be refused — revoked, out
                          of credit, typed wrong — and the provider's answer
                          names the key. The fix is on the same screen. */}
                      {mayManageAi && /api[\s_-]?key/i.test(aiError) ? (
                        <>
                          {' '}
                          <button type="button" className="an-link" onClick={openAiSettings}>
                            Check your AI settings
                          </button>
                        </>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>
              <div className="cmg-context-card" data-card="preview">
                <div className="cmg-context-title">{isSms ? 'On their phone' : 'In their inbox'}</div>
                {previewPanel}
              </div>
            </>
          )}

          {step === 3 && (
            <div className="cmg-context-card" data-card="how-it-goes">
              <div className="cmg-context-title">How it will go</div>
              <ol className="cmg-timeline">
                <li>
                  <b>
                    {draft?.status === 'scheduled' && draft.scheduledAt
                      ? fmtDateTime(draft.scheduledAt)
                      : whenMode === 'later'
                        ? 'At the time you set'
                        : 'When you send it'}
                  </b>
                  <span>Works out who matches at that moment</span>
                </li>
                <li>
                  <b>
                    {recipientCount === null ? 'The list' : recipientCount.toLocaleString()}{' '}
                    {isSms ? 'texts' : 'emails'}, up to {throttle} a minute
                  </b>
                  <span>
                    {isSmsOptions(deliveryOptions)
                      ? `${roughly(deliveryOptions.estimatedMinutes)}, and longer while agents are busy on this number`
                      : spreadDays
                        ? `Over about ${spreadDays} days — more than one day of this mailbox's allowance`
                        : recipientCount
                          ? `${roughly(recipientCount / Math.max(1, throttle))} at that pace`
                          : 'Paced so the mailbox never sends in a burst'}
                    {quietOn ? `, only ${minutesToClock(windowStart)}–${minutesToClock(windowEnd)} company time` : ''}
                  </span>
                </li>
                <li>
                  <b>Results fill in as it goes</b>
                  <span>{isSms ? 'Deliveries, taps and replies' : 'Opens, clicks and replies'}, then the reservations that followed</span>
                </li>
              </ol>
              <div className="cmg-context-title" style={{ marginTop: 6 }}>
                Left out at send
              </div>
              <div className="cmg-context-rows">
                <div className="cmg-context-row">
                  <span>Opted out</span>
                  <b>{contactOptedOut ? 'not skipped' : optedOutShown.toLocaleString()}</b>
                </div>
                <div className="cmg-context-row">
                  <span>Got another campaign recently</span>
                  <b>
                    {frequencyCap
                      ? `in the last ${FREQUENCY_CAPS.find((cap) => cap.hours === frequencyCap)?.label ?? `${frequencyCap} hours`}`
                      : 'not skipped'}
                  </b>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="cmg-context-card" data-card="summary">
              <div className="cmg-context-title">Summary</div>
              <div className="cmg-side-rows">
                <div className="cmg-side-row">
                  <span className="cmg-side-label">Name</span>
                  <span className="cmg-side-value">{name.trim() || '—'}</span>
                </div>
                <div className="cmg-side-row">
                  <span className="cmg-side-label">Audience</span>
                  <span className="cmg-side-value">{audienceLabel(audience)}</span>
                </div>
                {targetingLine && (
                  <div className="cmg-side-row">
                    <span className="cmg-side-label">Targeting</span>
                    <span className="cmg-side-value">{targetingLine}</span>
                  </div>
                )}
                <div className="cmg-side-row">
                  <span className="cmg-side-label">Recipients</span>
                  <span className="cmg-side-value">
                    {recipientCount === null ? '—' : recipientCount.toLocaleString()}
                  </span>
                </div>
                <div className="cmg-side-row">
                  <span className="cmg-side-label">From</span>
                  <span className="cmg-side-value">{selectedInbox ? identityLine(selectedInbox) : '—'}</span>
                </div>
                <div className="cmg-side-row">
                  <span className="cmg-side-label">Throttle</span>
                  <span className="cmg-side-value">{throttle} / min</span>
                </div>
                {sendModeSummary(deliveryOptions, effectiveSendMode) && (
                  <div className="cmg-side-row">
                    <span className="cmg-side-label">Send</span>
                    <span className="cmg-side-value">{sendModeSummary(deliveryOptions, effectiveSendMode)}</span>
                  </div>
                )}
                {draft?.status === 'scheduled' && draft.scheduledAt && (
                  <div className="cmg-side-row">
                    <span className="cmg-side-label">Scheduled</span>
                    <span className="cmg-side-value">{fmtDateTime(draft.scheduledAt)}</span>
                  </div>
                )}
              </div>
              <div className="cmg-warnbox">
                <TriangleAlert size={15} />
                {isSms ? (
                  <span>
                    "Sent" means the carrier accepted it, not that it arrived — Results shows the delivery
                    confirmations as they come back.
                  </span>
                ) : (
                  <span>
                    "Sent" means the server accepted the mail, not that it inboxed. Watch opens and bounces
                    in Results, and warm up new domains slowly.
                  </span>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function audienceLabel(audience: CampaignAudience): string {
  switch (audience) {
    case 'customers':
      return 'Customers';
    case 'affiliates':
      return 'Affiliates';
    case 'drivers':
      return 'Chauffeurs / drivers';
    case 'custom':
      return 'Custom list';
  }
}
